/**
 * frameGeometry.js
 *
 * Three.js geometry builders for rendering framed pictures.
 *
 * Coordinate convention:
 *   - 1 unit = 1 inch
 *   - The artwork is centered at the origin on the XY plane
 *   - X = horizontal, Y = vertical, Z = depth (toward viewer)
 *
 * Frame construction mirrors the server-side PIL logic:
 *   - 4 rails (top, bottom, left, right) with 45-degree mitered corners
 *   - Textures tiled along each rail's length
 *   - top_image on the face (repeated along length); side uses solid color
 */

import * as THREE from 'three'
import { round } from 'lodash'
import { corsCachebust } from '@/services/media.js'

// ---------------------------------------------------------------------------
// Texture helpers
// ---------------------------------------------------------------------------

const textureLoader = new THREE.TextureLoader()
textureLoader.crossOrigin = 'anonymous'

/**
 * Load a texture from a URL and configure it for tiling.
 * Returns a promise that resolves to the THREE.Texture.
 */
export function loadTexture(url) {
  return new Promise((resolve) => {
    if (!url) {
      resolve(null)
      return
    }
    const corsUrl = corsCachebust(url)
    textureLoader.load(
      corsUrl,
      (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace
        tex.wrapS = THREE.RepeatWrapping
        tex.wrapT = THREE.RepeatWrapping
        resolve(tex)
      },
      undefined,
      (err) => {
        console.warn('Failed to load texture:', url, err)
        resolve(null) // graceful fallback
      }
    )
  })
}

/**
 * Create a new texture with optional grayscale / sepia processing applied.
 * Returns the original texture when no filter is requested.
 *
 * @param {THREE.Texture|null} sourceTexture
 * @param {{ grayscale?: boolean, sepia?: boolean }|null} filter
 * @returns {THREE.Texture|null}
 */
export function createFilteredArtworkTexture(sourceTexture, filter = null) {
  if (!sourceTexture) return null

  const grayscale = Boolean(filter?.grayscale)
  const sepia = Boolean(filter?.sepia)
  if (!grayscale && !sepia) return sourceTexture

  const image = sourceTexture.image
  const width = image?.naturalWidth || image?.videoWidth || image?.width
  const height = image?.naturalHeight || image?.videoHeight || image?.height
  if (!image || !width || !height) return sourceTexture

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const ctx = canvas.getContext('2d')
  if (!ctx) return sourceTexture

  ctx.drawImage(image, 0, 0, width, height)

  const imageData = ctx.getImageData(0, 0, width, height)
  const d = imageData.data
  for (let i = 0; i < d.length; i += 4) {
    let r = d[i], g = d[i + 1], b = d[i + 2]
    if (grayscale) {
      const l = 0.299 * r + 0.587 * g + 0.114 * b
      r = g = b = l
    }
    if (sepia) {
      const sr = Math.min(255, r * 0.393 + g * 0.769 + b * 0.189)
      const sg = Math.min(255, r * 0.349 + g * 0.686 + b * 0.168)
      const sb = Math.min(255, r * 0.272 + g * 0.534 + b * 0.131)
      r = sr; g = sg; b = sb
    }
    d[i] = r; d[i + 1] = g; d[i + 2] = b
  }
  ctx.putImageData(imageData, 0, 0)

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.needsUpdate = true
  return tex
}

/**
 * Load frame texture from extra_data. Only top_image is used for texturing.
 * Other frame images are reference photos and should not be tiled onto geometry.
 */
export async function loadFrameTextures(extraData) {
  if (!extraData) return { front: null, side: null, top: null, sampledColor: null }
  const top = await loadTexture(extraData.top_image)
  const sampledColor = sampleFrameColor(top?.image)
  return { front: null, side: null, top, sampledColor }
}

function sampleFrameColor(image) {
  if (!image) return null
  try {
    const width = image.naturalWidth || image.videoWidth || image.width
    const height = image.naturalHeight || image.videoHeight || image.height
    if (!width || !height) return null
    const sampleSize = 16
    const canvas = document.createElement('canvas')
    canvas.width = sampleSize
    canvas.height = sampleSize
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return null
    ctx.drawImage(image, 0, 0, sampleSize, sampleSize)
    const { data } = ctx.getImageData(0, 0, sampleSize, sampleSize)
    let r = 0; let g = 0; let b = 0; let count = 0
    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3]
      if (alpha < 16) continue
      r += data[i]
      g += data[i + 1]
      b += data[i + 2]
      count += 1
    }
    if (!count) return null
    return new THREE.Color(r / (255 * count), g / (255 * count), b / (255 * count)).getHex()
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Geometry builders
// ---------------------------------------------------------------------------

/**
 * Inches-to-scene coordinate helper (identity since 1 unit = 1 inch,
 * but provides a single place to change the scale if needed).
 */
const SCALE = 1
export function inches(val) {
  return Number(val) * SCALE
}

const DEFAULT_MAT_DEPTH = 0.0625
const DEFAULT_MAT_CORE_TARGET = '#f7f3ea'

// ---------------------------------------------------------------------------
// Faked beveled-edge rim (applied to rigid substrates and canvas wraps)
//
// A thin extruded ring sits around the panel's front face to fake a slightly
// rounded edge that catches specular highlights. The flat front/back caps are
// invisible; only the curved bevel surface shows, so the underlying artwork
// remains visible everywhere except the outermost perimeter sliver.
//
// The rim uses additive blending with a black base + metal reflection, so it
// contributes nothing where it's facing away from light and glints brightly
// where it catches the IBL. Tune intensity via `intensity` and sharpness via
// `roughness`.
//
// The settings object is intentionally mutable so a debug UI can tweak values
// live — call `refreshPanelBevelRims(root)` after changing any value to
// rebuild the geometry/material of already-mounted rims.
// ---------------------------------------------------------------------------
export const panelBevelSettings = {
  sizeInches: 0.1,          // horizontal + vertical bevel radius (canvas default)
  segments: 10,             // smoothness of the curve
  roughness: 1.0,           // lower = tighter / sharper glint (affects metal lobe)
  metalness: 0.0,           // higher = more metallic sheen
  clearcoat: 0.41,          // extra glossy top layer (main source of highlight)
  clearcoatRoughness: 0.58,
  intensity: 1.1,           // multiplier on final rim output (affects all lobes)
  zOffsetInches: -0.02,     // push rim forward/back of front face
  canvasNormalScale: 3.0,   // strength of the canvas weave normal on the rim
  canvasNormalTileInches: 2.5, // physical size of one weave tile (match canvas)
  debugUnlit: false,        // flat magenta for mesh-visibility debugging
  debugColor: 0xff00ff,
}

// Rigid substrates (aluminum, acrylic, gator board) use a tighter bead than
// fabric wrap (canvas / acoustic panel) — hard, machined edges vs a wrapped face.
export const RIGID_PANEL_BEVEL_SIZE_INCHES = 0.05

/**
 * Create a flat plate geometry with a capsule-shaped (pill) hole in the center.
 * @param {number} plateW - plate width (x)
 * @param {number} plateH - plate height (y)
 * @param {number} holeW - capsule hole width
 * @param {number} holeH - capsule hole height (short axis)
 * @param {number} thick - extrusion depth (z)
 * @returns {THREE.BufferGeometry}
 */
function createPlateWithCapsuleHole(plateW, plateH, holeW, holeH, thick) {
  const halfW = plateW / 2
  const halfH = plateH / 2
  const shape = new THREE.Shape()
  shape.moveTo(-halfW, -halfH)
  shape.lineTo(halfW, -halfH)
  shape.lineTo(halfW, halfH)
  shape.lineTo(-halfW, halfH)
  shape.closePath()

  // Capsule hole: horizontal pill (two semicircles + rect). Wound opposite to outer for hole.
  const r = holeH / 2
  const cx = holeW / 2 - r
  const hole = new THREE.Path()
  hole.moveTo(-cx, r)
  hole.absarc(-cx, 0, r, Math.PI / 2, -Math.PI / 2)
  hole.lineTo(cx, -r)
  hole.absarc(cx, 0, r, -Math.PI / 2, Math.PI / 2)
  hole.closePath()
  shape.holes.push(hole)

  const geom = new THREE.ExtrudeGeometry(shape, { depth: thick, bevelEnabled: false })
  geom.center()
  return geom
}

function createRectPath({ left, right, top, bottom }) {
  const path = new THREE.Path()
  path.moveTo(left, bottom)
  path.lineTo(right, bottom)
  path.lineTo(right, top)
  path.lineTo(left, top)
  path.closePath()
  return path
}

function createRectShape(bounds) {
  const shape = new THREE.Shape()
  shape.moveTo(bounds.left, bounds.bottom)
  shape.lineTo(bounds.right, bounds.bottom)
  shape.lineTo(bounds.right, bounds.top)
  shape.lineTo(bounds.left, bounds.top)
  shape.closePath()
  return shape
}

function deriveMatCoreColor(color, coreColor = null) {
  if (coreColor) return new THREE.Color(coreColor)
  const face = new THREE.Color(color || '#FFFFFF')
  const coreTarget = new THREE.Color(DEFAULT_MAT_CORE_TARGET)
  return face.clone().lerp(coreTarget, 0.7)
}

function getMatBevelSettings(widths, depth) {
  const clampedDepth = Math.max(0, Number(depth) || 0)
  const bevelSize = clampedDepth
  const bevelEnabled = bevelSize > 1e-4

  return {
    bevelEnabled,
    bevelSize,
    bevelThickness: bevelSize,
  }
}

function createMatGeometry({
  innerBounds,
  widths,
  depth = DEFAULT_MAT_DEPTH,
}) {
  const safeWidths = {
    top: Math.max(0, Number(widths?.top) || 0),
    bottom: Math.max(0, Number(widths?.bottom) || 0),
    left: Math.max(0, Number(widths?.left) || 0),
    right: Math.max(0, Number(widths?.right) || 0),
  }
  const epsilon = 1e-4
  const outerBounds = {
    left: innerBounds.left - Math.max(safeWidths.left, epsilon),
    right: innerBounds.right + Math.max(safeWidths.right, epsilon),
    top: innerBounds.top + Math.max(safeWidths.top, epsilon),
    bottom: innerBounds.bottom - Math.max(safeWidths.bottom, epsilon),
  }

  const d = Math.max(epsilon, inches(depth))
  const shape = createRectShape(outerBounds)
  shape.holes.push(createRectPath(innerBounds))

  const bevel = getMatBevelSettings(safeWidths, d)
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: d,
    bevelEnabled: bevel.bevelEnabled,
    bevelSize: bevel.bevelSize,
    bevelThickness: bevel.bevelThickness,
    bevelSegments: bevel.bevelEnabled ? 1 : 0,
    curveSegments: 1,
    steps: 1,
  })
  geometry.computeVertexNormals()

  return { geometry, outerBounds, safeWidths }
}

function createMatMesh({
  innerBounds,
  widths,
  color = '#FFFFFF',
  depth = DEFAULT_MAT_DEPTH,
  coreColor = null,
  matNum = 1,
}) {
  const { geometry, outerBounds, safeWidths } = createMatGeometry({ innerBounds, widths, depth })

  const faceMaterial = new THREE.MeshStandardMaterial({
    color: new THREE.Color(color || '#FFFFFF'),
    roughness: 0.92,
    metalness: 0,
  })
  const coreMaterial = new THREE.MeshStandardMaterial({
    color: deriveMatCoreColor(color, coreColor),
    roughness: 0.88,
    metalness: 0,
  })

  const mesh = new THREE.Mesh(geometry, [faceMaterial, coreMaterial])
  mesh.userData = {
    type: 'mat-piece',
    label: `Mat ${matNum}`,
    matNum,
    widthInches: safeWidths.top,
    widthsInches: { ...safeWidths },
    innerBounds: { ...innerBounds },
    outerBounds: { ...outerBounds },
    depthInches: depth,
  }

  return { mesh, outerBounds }
}

/**
 * Custom UV generator for frame rail front face.
 * - U: along rail length (0-1 maps full length)
 * - V: cross rail (inner→outer), with V=0 at outer edge so texture top aligns with outer frame
 * - Texture height maps 1:1 to rail width (1 UV unit = frameWidth inches)
 */
function createRailUVGenerator(inner, fw) {
  const xMin = -inner / 2 - fw
  const xLen = inner + 2 * fw
  const yMax = Math.max(fw, 1e-6)

  return {
    generateTopUV: function (geometry, vertices, indexA, indexB, indexC) {
      const toUV = (i) => {
        const x = vertices[i * 3]
        const y = vertices[i * 3 + 1]
        const u = xLen > 0 ? (x - xMin) / xLen : 0
        const v = y / yMax // V=0 at inner (y=0), V=1 at outer (y=fw) → texture top at outer (inverted for correct orientation)
        return new THREE.Vector2(u, v)
      }
      return [toUV(indexA), toUV(indexB), toUV(indexC)]
    },
    generateSideWallUV: function (geometry, vertices, indexA, indexB, indexC, indexD) {
      // Side faces use default-style; we only texture the front
      const a_z = vertices[indexA * 3 + 2]
      const b_z = vertices[indexB * 3 + 2]
      const c_z = vertices[indexC * 3 + 2]
      const d_z = vertices[indexD * 3 + 2]
      return [
        new THREE.Vector2(0, 1 - a_z),
        new THREE.Vector2(1, 1 - b_z),
        new THREE.Vector2(1, 1 - c_z),
        new THREE.Vector2(0, 1 - d_z),
      ]
    },
  }
}

function normalizeFrameColor(frameColor, fallback = 0x8B7355) {
  if (frameColor == null || frameColor === '') return new THREE.Color(fallback)
  try {
    return new THREE.Color(frameColor)
  } catch {
    return new THREE.Color(fallback)
  }
}

function getFrameColorPalette(frameColor) {
  const front = normalizeFrameColor(frameColor, 0x8B7355)
  const hsl = {}
  front.getHSL(hsl)
  const side = new THREE.Color().setHSL(
    hsl.h,
    Math.min(1, hsl.s * 1.1),
    Math.max(0, hsl.l * 0.7)
  )
  return {
    front,
    side,
  }
}

function rotateFrameTopTexture(tex) {
  if (!tex) return
  tex.center.set(0.5, 0.5)
  tex.rotation = Math.PI
}

/**
 * UV generator for float frame back legs - matches front rail mapping.
 * U: along leg length, V: across leg (inner→outer), same scale as front (1 UV unit = fw in world).
 */
function createBackLegUVGenerator(outerLeft, outerRight, outerBottom, outerTop, fw, legWidth, position) {
  const railLen = outerRight - outerLeft
  const railH = outerTop - outerBottom
  const vScale = Math.max(fw, 1e-6)

  const toUV = (x, y) => {
    let u, v
    if (position === 'top') {
      u = railLen > 0 ? (x - outerLeft) / railLen : 0
      v = (y - (outerTop - legWidth)) / vScale
    } else if (position === 'bottom') {
      u = railLen > 0 ? (x - outerLeft) / railLen : 0
      v = (outerBottom + legWidth - y) / vScale
    } else if (position === 'left') {
      u = railH > 0 ? (y - outerBottom) / railH : 0
      v = (x - outerLeft) / vScale
    } else {
      u = railH > 0 ? (y - outerBottom) / railH : 0
      v = (outerRight - x) / vScale
    }
    return new THREE.Vector2(u, v)
  }

  return {
    generateTopUV: function (geometry, vertices, indexA, indexB, indexC) {
      const uv = (i) => toUV(vertices[i * 3], vertices[i * 3 + 1])
      return [uv(indexA), uv(indexB), uv(indexC)]
    },
    generateBottomUV: function (geometry, vertices, indexA, indexB, indexC) {
      return this.generateTopUV(geometry, vertices, indexA, indexB, indexC)
    },
    generateSideWallUV: function (geometry, vertices, indexA, indexB, indexC, indexD) {
      const a_z = vertices[indexA * 3 + 2]
      const b_z = vertices[indexB * 3 + 2]
      const c_z = vertices[indexC * 3 + 2]
      const d_z = vertices[indexD * 3 + 2]
      return [
        new THREE.Vector2(0, 1 - a_z),
        new THREE.Vector2(1, 1 - b_z),
        new THREE.Vector2(1, 1 - c_z),
        new THREE.Vector2(0, 1 - d_z),
      ]
    },
  }
}

/**
 * Apply "cover" fill to artwork texture for given plane aspect ratio.
 * Used as a fallback when no explicit crop window is provided.
 * @param {THREE.Texture|null} tex
 * @param {number} planeAspect - width/height of the plane
 */
export function applyArtworkTextureCover(tex, planeAspect) {
  if (!tex || !tex.image) return
  tex.wrapS = THREE.ClampToEdgeWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping
  const imgW = tex.image.width || tex.image.naturalWidth
  const imgH = tex.image.height || tex.image.naturalHeight
  if (!imgW || !imgH) return
  const imgAspect = imgW / imgH
  if (imgAspect > planeAspect) {
    const scale = planeAspect / imgAspect
    tex.offset.x = (1 - scale) / 2
    tex.repeat.x = scale
    tex.offset.y = 0
    tex.repeat.y = 1
  } else {
    const scale = imgAspect / planeAspect
    tex.offset.y = (1 - scale) / 2
    tex.repeat.y = scale
    tex.offset.x = 0
    tex.repeat.x = 1
  }
}

/**
 * Apply an explicit crop window to the artwork texture.
 *
 * All arguments are expressed in inches in artwork space:
 * - planeWidth / planeHeight: final artwork size in inches.
 * - fullWidth / fullHeight: source image size in inches at the chosen scale.
 * - cropLeft / cropTop: offsets from the top-left of the full image to the
 *   top-left corner of the visible window, in inches.
 *
 * Converts these to texture repeat/offset values in UV space, accounting for the
 * fact that Three.js UV origin is bottom-left while cropTop is measured from top.
 *
 * @param {THREE.Texture|null} tex
 * @param {number} planeWidth
 * @param {number} planeHeight
 * @param {number} fullWidth
 * @param {number} fullHeight
 * @param {number} cropLeft
 * @param {number} cropTop
 */
function applyArtworkTextureCropWindow(
  tex,
  planeWidth,
  planeHeight,
  fullWidth,
  fullHeight,
  cropLeft,
  cropTop,
  scaleX = 1,
  scaleY = 1,
  rotation = 0
) {
  if (!tex) return
  tex.wrapS = THREE.ClampToEdgeWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping

  const pw = Number(planeWidth)
  const ph = Number(planeHeight)
  const uw = Number(fullWidth)
  const uh = Number(fullHeight)

  if (!Number.isFinite(pw) || !Number.isFinite(ph) || pw <= 0 || ph <= 0) return
  if (!Number.isFinite(uw) || !Number.isFinite(uh) || uw <= 0 || uh <= 0) return

  const window = resolveArtworkBaseWindow({
    windowWidth: pw,
    windowHeight: ph,
    fullWidth: uw,
    fullHeight: uh,
    cropLeft,
    cropTop,
    scaleX,
    scaleY,
    rotation,
  })

  const rot = normalizeArtworkRotation(rotation)
  const sourceDrawW = rot % 180 === 90 ? window.fullHeight : window.fullWidth
  const sourceDrawH = rot % 180 === 90 ? window.fullWidth : window.fullHeight
  const mapUv = (u, v) => {
    const planeX = u * pw
    const planeY = (1 - v) * ph
    const visualX = window.cropLeft + planeX
    const visualY = window.cropTop + planeY
    const dx = visualX - window.fullWidth / 2
    const dy = visualY - window.fullHeight / 2
    let sampleRelX = dx
    let sampleRelY = dy
    if (rot === 90) {
      sampleRelX = dy
      sampleRelY = -dx
    } else if (rot === 180) {
      sampleRelX = -dx
      sampleRelY = -dy
    } else if (rot === 270) {
      sampleRelX = -dy
      sampleRelY = dx
    }
    const sampleX = sourceDrawW / 2 + sampleRelX * window.sxSign
    const sampleY = sourceDrawH / 2 + sampleRelY * window.sySign
    return {
      u: sampleX / sourceDrawW,
      v: 1 - (sampleY / sourceDrawH),
    }
  }
  const origin = mapUv(0, 0)
  const xAxis = mapUv(1, 0)
  const yAxis = mapUv(0, 1)

  tex.matrixAutoUpdate = false
  tex.center.set(0, 0)
  tex.rotation = 0
  tex.offset.set(0, 0)
  tex.repeat.set(1, 1)
  tex.matrix.set(
    xAxis.u - origin.u,
    yAxis.u - origin.u,
    origin.u,
    xAxis.v - origin.v,
    yAxis.v - origin.v,
    origin.v,
    0,
    0,
    1
  )
  tex.needsUpdate = true
}

function getArtworkSourceTexture(artworkTexture) {
  return artworkTexture?.userData?.sourceTexture || artworkTexture || null
}

export function createArtworkUvTransformTexture(artworkTexture, {
  planeWidth,
  planeHeight,
  fullWidth,
  fullHeight,
  cropLeft,
  cropTop,
  scaleX = 1,
  scaleY = 1,
  rotation = 0,
} = {}) {
  const sourceTexture = getArtworkSourceTexture(artworkTexture)
  if (!sourceTexture) return null
  const tex = sourceTexture.clone()
  tex.userData = {
    ...(tex.userData || {}),
    sourceTexture,
  }
  applyArtworkTextureCropWindow(
    tex,
    planeWidth,
    planeHeight,
    fullWidth,
    fullHeight,
    cropLeft,
    cropTop,
    scaleX,
    scaleY,
    rotation
  )
  return tex
}

export function resolveArtworkUvTransformTexture(existingTexture, artworkTexture, opts = {}) {
  const sourceTexture = getArtworkSourceTexture(artworkTexture || existingTexture)
  if (!sourceTexture) return null
  const existingSource = existingTexture?.userData?.sourceTexture
  if (existingTexture && existingSource === sourceTexture) {
    existingTexture.userData = {
      ...(existingTexture.userData || {}),
      sourceTexture,
    }
    applyArtworkTextureCropWindow(
      existingTexture,
      opts.planeWidth,
      opts.planeHeight,
      opts.fullWidth,
      opts.fullHeight,
      opts.cropLeft,
      opts.cropTop,
      opts.scaleX,
      opts.scaleY,
      opts.rotation
    )
    return existingTexture
  }
  return createArtworkUvTransformTexture(sourceTexture, opts)
}

function resolveArtworkBaseWindow({
  windowWidth,
  windowHeight,
  fullWidth,
  fullHeight,
  cropLeft,
  cropTop,
  scaleX = 1,
  scaleY = 1,
}) {
  const safeFullWidth = Math.max(Number(fullWidth) || windowWidth, windowWidth)
  const safeFullHeight = Math.max(Number(fullHeight) || windowHeight, windowHeight)
  const sxSign = Number(scaleX) < 0 ? -1 : 1
  const sySign = Number(scaleY) < 0 ? -1 : 1
  const maxCropLeft = Math.max(0, safeFullWidth - windowWidth)
  const maxCropTop = Math.max(0, safeFullHeight - windowHeight)
  return {
    fullWidth: safeFullWidth,
    fullHeight: safeFullHeight,
    cropLeft: Math.min(Math.max(0, Number(cropLeft) || 0), maxCropLeft),
    cropTop: Math.min(Math.max(0, Number(cropTop) || 0), maxCropTop),
    sxSign,
    sySign,
  }
}

export function rotateArtworkOffsetRatio(x, y, rotation = 0) {
  const rot = normalizeArtworkRotation(rotation)
  if (rot === 90) return { x: y, y: -x }
  if (rot === 180) return { x: -x, y: -y }
  if (rot === 270) return { x: -y, y: x }
  return { x, y }
}

function normalizeArtworkRotation(rotation) {
  const n = Number(rotation) || 0
  return ((Math.round(n / 90) * 90) % 360 + 360) % 360
}

function getRotatedArtworkAspect(imageAspect, rotation) {
  const aspect = Number(imageAspect)
  if (!Number.isFinite(aspect) || aspect <= 0) return aspect
  return normalizeArtworkRotation(rotation) % 180 === 90 ? 1 / aspect : aspect
}

function drawArtworkWindow(ctx, {
  image,
  destX,
  destY,
  destW,
  destH,
  windowWidth,
  windowHeight,
  fullWidth,
  fullHeight,
  cropLeft,
  cropTop,
  scaleX = 1,
  scaleY = 1,
  rotation = 0,
}) {
  if (!ctx || !image || destW <= 0 || destH <= 0 || windowWidth <= 0 || windowHeight <= 0) return

  const imgW = image.width || image.naturalWidth || image.videoWidth
  const imgH = image.height || image.naturalHeight || image.videoHeight
  if (!imgW || !imgH) return

  const rot = normalizeArtworkRotation(rotation)
  const window = resolveArtworkBaseWindow({
    windowWidth,
    windowHeight,
    fullWidth,
    fullHeight,
    cropLeft,
    cropTop,
    scaleX,
    scaleY,
    rotation,
  })
  const sourceDrawW = rot % 180 === 90 ? window.fullHeight : window.fullWidth
  const sourceDrawH = rot % 180 === 90 ? window.fullWidth : window.fullHeight

  ctx.save()
  ctx.beginPath()
  ctx.rect(destX, destY, destW, destH)
  ctx.clip()
  ctx.translate(destX, destY)
  ctx.scale(destW / windowWidth, destH / windowHeight)
  ctx.translate(-window.cropLeft, -window.cropTop)
  ctx.translate(window.fullWidth / 2, window.fullHeight / 2)
  ctx.rotate((rot * Math.PI) / 180)
  ctx.scale(window.sxSign, window.sySign)
  ctx.translate(-sourceDrawW / 2, -sourceDrawH / 2)
  ctx.drawImage(
    image,
    0,
    0,
    sourceDrawW,
    sourceDrawH
  )
  ctx.restore()
}

export function createArtworkWindowTexture(artworkTexture, {
  planeWidth,
  planeHeight,
  fullWidth,
  fullHeight,
  cropLeft,
  cropTop,
  scaleX = 1,
  scaleY = 1,
  rotation = 0,
  base = 1536,
} = {}) {
  const image = artworkTexture?.image
  const pw = Number(planeWidth)
  const ph = Number(planeHeight)
  if (!image || !(pw > 0) || !(ph > 0)) return artworkTexture || null

  const canvasSize = getSubstrateFrontCanvasSize(pw, ph, base)
  const canvas = document.createElement('canvas')
  canvas.width = canvasSize.width
  canvas.height = canvasSize.height
  const ctx = canvas.getContext('2d')
  if (!ctx) return artworkTexture || null
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  drawArtworkWindow(ctx, {
    image,
    destX: 0,
    destY: 0,
    destW: canvas.width,
    destH: canvas.height,
    windowWidth: pw,
    windowHeight: ph,
    fullWidth,
    fullHeight,
    cropLeft,
    cropTop,
    scaleX,
    scaleY,
    rotation,
  })

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.wrapS = THREE.ClampToEdgeWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping
  tex.userData = {
    ...(tex.userData || {}),
    sourceTexture: artworkTexture?.userData?.sourceTexture || artworkTexture || null,
  }
  tex.needsUpdate = true
  return tex
}

/**
 * Convert the simplified artwork transform model (scale + offset) to the
 * crop window consumed by applyArtworkTextureCropWindow().
 *
 * At scale 1 / offset 0,0 the image is sized to cover-fit the artwork opening
 * and centered.  Increasing scale zooms in; positive offsets move the image
 * right / down.
 *
 * @param {number} width        Artwork opening width (inches)
 * @param {number} height       Artwork opening height (inches)
 * @param {number} scale        Zoom factor (1 = cover-fit)
 * @param {number} offsetLeft   Horizontal displacement from center (inches)
 * @param {number} offsetTop    Vertical displacement from center (inches)
 * @param {number} imageAspect  Source image natural width / height
 * @param {number} precision    Number of decimal places to round to
 * @returns {{ fullWidth: number, fullHeight: number, cropLeft: number, cropTop: number, width: number, height: number }}
 */
export function getCropFromScale(width, height, scale, offsetLeft, offsetTop, imageAspect, precision = 3, scaleY = scale, rotation = 0) {
  const w = Number(width)
  const h = Number(height)
  const sx = Math.max(1, Math.abs(Number(scale) || 1))
  const sy = Math.max(1, Math.abs(Number(scaleY) || sx))
  const ol = Number(offsetLeft) || 0
  const ot = Number(offsetTop) || 0
  const aspect = getRotatedArtworkAspect(imageAspect, rotation)

  if ( !Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0
    || !Number.isFinite(aspect) || aspect <= 0
  ) {
    return { fullWidth: w, fullHeight: h, cropLeft: 0, cropTop: 0, width: w, height: h }
  }

  const baseCoverWidth = Math.max(w, h * aspect)
  const baseCoverHeight = baseCoverWidth / aspect

  const fullWidth = baseCoverWidth * sx
  const fullHeight = baseCoverHeight * sy

  const cropLeft = (fullWidth - w) / 2 - ol
  const cropTop = (fullHeight - h) / 2 - ot

  return {
    fullWidth: round(fullWidth, precision),
    fullHeight: round(fullHeight, precision),
    cropLeft: round(cropLeft, precision),
    cropTop: round(cropTop, precision),
    width: round(w, precision),
    height: round(h, precision),
  }
}

/**
 * Create a repeating alpha texture for perforated film (grid of holes).
 * 4 holes per inch, hole diameter ~0.0625", pattern repeats every 0.25".
 * @returns {THREE.DataTexture}
 */
function createPerforatedHoleTexture() {
  const size = 128
  const holeRadius = ((0.0625 / 2) / 0.25) * size
  const data = new Uint8Array(size * size * 4)
  const cx = size / 2
  const r2 = holeRadius * holeRadius
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = x - cx + 0.5
      const dy = y - cx + 0.5
      const inHole = dx * dx + dy * dy <= r2
      const v = inHole ? 0 : 255
      const i = (y * size + x) * 4
      data[i] = data[i + 1] = data[i + 2] = v
      data[i + 3] = 255
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat)
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.needsUpdate = true
  return tex
}

export function applyPerforationAlphaMapScale(material, width, height) {
  const mat = Array.isArray(material) ? material[0] : material
  const alphaMap = mat?.alphaMap
  const holesPerInch = alphaMap?.userData?.holesPerInch || mat?.userData?.holesPerInch || 4
  if (!alphaMap || !(holesPerInch > 0) || !(width > 0) || !(height > 0)) return

  const rx = width * holesPerInch
  const ry = height * holesPerInch
  alphaMap.repeat.set(rx, ry)
  alphaMap.offset.set(0.5 * (1 - rx), 0.5 * (1 - ry))
  alphaMap.needsUpdate = true
}

/**
 * Create the artwork plane.
 *
 * @param {number} width  - artwork width in inches
 * @param {number} height - artwork height in inches
 * @param {THREE.Texture|null} artworkTexture
 * @param {object} opts - optional: { side, opacity, transparent, perforated, holesPerInch }
 * @returns {THREE.Mesh}
 */
export function createArtworkPlane(width, height, artworkTexture, opts = {}) {
  const w = inches(width)
  const h = inches(height)
  const geo = new THREE.PlaneGeometry(w, h)
  let displayTexture = artworkTexture

  if (
    opts.fullWidth != null &&
    opts.fullHeight != null &&
    opts.cropLeft != null &&
    opts.cropTop != null
  ) {
    displayTexture = createArtworkUvTransformTexture(artworkTexture, {
      planeWidth: width,
      planeHeight: height,
      fullWidth: opts.fullWidth,
      fullHeight: opts.fullHeight,
      cropLeft: opts.cropLeft,
      cropTop: opts.cropTop,
      scaleX: opts.scaleX,
      scaleY: opts.scaleY,
      rotation: opts.rotation,
    })
  } else {
    applyArtworkTextureCover(displayTexture, w / h)
  }

  const side = opts.side ?? THREE.FrontSide
  const opacity = opts.opacity ?? 1
  const transparent = opts.transparent ?? (opacity < 1)
  const perforated = opts.perforated ?? false

  const matOpts = {
    map: displayTexture || null,
    color: displayTexture ? 0xffffff : 0xdddddd,
    side,
    opacity,
    transparent,
  }
  if (perforated) {
    const holesPerInch = opts.holesPerInch || 4
    const holeTex = createPerforatedHoleTexture()
    holeTex.userData = {
      holesPerInch,
      holeSizeInches: opts.holeSizeInches || 0.0625,
    }
    matOpts.alphaMap = holeTex
    matOpts.transparent = true
    matOpts.alphaTest = 0.01
  }
  const mat = new THREE.MeshBasicMaterial(matOpts)
  if (perforated) {
    mat.userData.holesPerInch = opts.holesPerInch || 4
    applyPerforationAlphaMapScale(mat, width, height)
  }
  const mesh = new THREE.Mesh(geo, mat)
  mesh.position.set(0, 0, 0)
  mesh.userData = {
    type: 'artwork',
    label: 'Artwork',
    widthInches: width,
    heightInches: height,
    artworkTexture,
  }
  return mesh
}

function mirrorFillMargins(canvas, bounds) {
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  const { left, top, right, bottom } = bounds
  const centerX = left
  const centerY = top
  const centerW = canvas.width - left - right
  const centerH = canvas.height - top - bottom

  if (left > 0) {
    ctx.save()
    ctx.translate(left, 0)
    ctx.scale(-1, 1)
    ctx.drawImage(canvas, centerX, centerY, left, centerH, 0, centerY, left, centerH)
    ctx.restore()
  }
  if (right > 0) {
    ctx.save()
    ctx.translate(canvas.width - right, 0)
    ctx.scale(-1, 1)
    ctx.drawImage(
      canvas,
      centerX + centerW - right,
      centerY,
      right,
      centerH,
      -right,
      centerY,
      right,
      centerH
    )
    ctx.restore()
  }
  if (top > 0) {
    ctx.save()
    ctx.translate(0, top)
    ctx.scale(1, -1)
    ctx.drawImage(canvas, 0, centerY, canvas.width, top, 0, 0, canvas.width, top)
    ctx.restore()
  }
  if (bottom > 0) {
    ctx.save()
    ctx.translate(0, canvas.height - bottom)
    ctx.scale(1, -1)
    ctx.drawImage(
      canvas,
      0,
      centerY + centerH - bottom,
      canvas.width,
      bottom,
      0,
      -bottom,
      canvas.width,
      bottom
    )
    ctx.restore()
  }
}

function getCanvasWrapTextureLayout(artWidth, artHeight, sideDepth, backReturn, base = 1536) {
  const front = getSubstrateFrontCanvasSize(artWidth, artHeight, base)
  const padXRatio = (sideDepth + backReturn) / Math.max(artWidth, 1e-6)
  const padYRatio = (sideDepth + backReturn) / Math.max(artHeight, 1e-6)
  const padLeft = Math.max(1, Math.round(front.width * padXRatio))
  const padRight = padLeft
  const padTop = Math.max(1, Math.round(front.height * padYRatio))
  const padBottom = padTop
  return {
    width: front.width + padLeft + padRight,
    height: front.height + padTop + padBottom,
    frontWidth: front.width,
    frontHeight: front.height,
    padLeft,
    padRight,
    padTop,
    padBottom,
    sideDepth,
    backReturn,
  }
}

function drawCanvasWrapTexture({
  canvas,
  layout,
  artworkTexture,
  artworkWidth,
  artworkHeight,
  fullWidth,
  fullHeight,
  cropLeft,
  cropTop,
  scaleX = 1,
  scaleY = 1,
  rotation = 0,
}) {
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  const image = artworkTexture?.image
  if (!image || artworkWidth <= 0 || artworkHeight <= 0) return

  const imgW = image.width || image.naturalWidth || image.videoWidth
  const imgH = image.height || image.naturalHeight || image.videoHeight
  if (!imgW || !imgH) return

  const bleedX = layout.sideDepth + layout.backReturn
  const bleedY = layout.sideDepth + layout.backReturn
  const desiredFullWidth = artworkWidth + 2 * bleedX
  const desiredFullHeight = artworkHeight + 2 * bleedY
  const desiredCropLeft = (Number(cropLeft) || 0) - bleedX
  const desiredCropTop = (Number(cropTop) || 0) - bleedY
  const safeFullWidth = Math.max(Number(fullWidth) || artworkWidth, artworkWidth)
  const safeFullHeight = Math.max(Number(fullHeight) || artworkHeight, artworkHeight)

  const availableLeft = Math.max(0, desiredCropLeft)
  const availableTop = Math.max(0, desiredCropTop)
  const availableRight = Math.min(safeFullWidth, desiredCropLeft + desiredFullWidth)
  const availableBottom = Math.min(safeFullHeight, desiredCropTop + desiredFullHeight)
  const availableWidth = Math.max(0, availableRight - availableLeft)
  const availableHeight = Math.max(0, availableBottom - availableTop)

  const leftMissing = Math.max(0, -desiredCropLeft)
  const topMissing = Math.max(0, -desiredCropTop)
  const destX = (leftMissing / desiredFullWidth) * canvas.width
  const destY = (topMissing / desiredFullHeight) * canvas.height
  const destW = (availableWidth / desiredFullWidth) * canvas.width
  const destH = (availableHeight / desiredFullHeight) * canvas.height

  if (availableWidth > 0 && availableHeight > 0) {
    drawArtworkWindow(ctx, {
      image,
      destX,
      destY,
      destW,
      destH,
      windowWidth: availableWidth,
      windowHeight: availableHeight,
      fullWidth: safeFullWidth,
      fullHeight: safeFullHeight,
      cropLeft: availableLeft,
      cropTop: availableTop,
      scaleX,
      scaleY,
      rotation,
    })
  }

  mirrorFillMargins(canvas, {
    left: Math.max(0, Math.round(destX)),
    top: Math.max(0, Math.round(destY)),
    right: Math.max(0, Math.round(canvas.width - destX - destW)),
    bottom: Math.max(0, Math.round(canvas.height - destY - destH)),
  })
}

function drawCroppedArtworkToRect(ctx, {
  image,
  destX,
  destY,
  destW,
  destH,
  artworkWidth,
  artworkHeight,
  fullWidth,
  fullHeight,
  cropLeft,
  cropTop,
  scaleX = 1,
  scaleY = 1,
  rotation = 0,
}) {
  drawArtworkWindow(ctx, {
    image,
      destX,
      destY,
      destW,
    destH,
    windowWidth: artworkWidth,
    windowHeight: artworkHeight,
    fullWidth,
    fullHeight,
    cropLeft,
    cropTop,
    scaleX,
    scaleY,
    rotation,
  })
}

function createCanvasTextureSlice(baseTexture, repeat, offset) {
  const tex = baseTexture.clone()
  tex.repeat.copy(repeat)
  tex.offset.copy(offset)
  tex.wrapS = THREE.ClampToEdgeWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping
  tex.needsUpdate = true
  return tex
}

function createRepeatedCanvasTexture(baseTexture, repeat, offset = new THREE.Vector2(0, 0)) {
  const tex = baseTexture.clone()
  tex.repeat.copy(repeat)
  tex.offset.copy(offset)
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.needsUpdate = true
  return tex
}

let _canvasWeaveBaseTexture = null
let _canvasWeaveNormalBaseTexture = null
function getCanvasWeaveBaseTexture() {
  if (_canvasWeaveBaseTexture) return _canvasWeaveBaseTexture

  const size = 256
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  const img = ctx.createImageData(size, size)
  const data = img.data
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4
      const warp = Math.sin((x / size) * Math.PI * 2 * 28)
      const weft = Math.sin((y / size) * Math.PI * 2 * 30)
      const cross = Math.sin(((x + y) / size) * Math.PI * 2 * 12)
      const grain = ((x * 17 + y * 31) % 19) / 19 - 0.5
      const v = Math.max(0, Math.min(255, Math.round(128 + warp * 22 + weft * 18 + cross * 8 + grain * 10)))
      data[i] = v
      data[i + 1] = v
      data[i + 2] = v
      data[i + 3] = 255
    }
  }
  ctx.putImageData(img, 0, 0)

  const tex = new THREE.CanvasTexture(canvas)
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.colorSpace = THREE.NoColorSpace
  tex.needsUpdate = true
  _canvasWeaveBaseTexture = tex
  return tex
}

function getCanvasWeaveNormalBaseTexture() {
  if (_canvasWeaveNormalBaseTexture) return _canvasWeaveNormalBaseTexture

  const heightTex = getCanvasWeaveBaseTexture()
  const sourceCanvas = heightTex?.image
  if (!sourceCanvas) return null

  const size = sourceCanvas.width
  const srcCtx = sourceCanvas.getContext?.('2d')
  if (!srcCtx) return null
  const src = srcCtx.getImageData(0, 0, size, size).data

  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  const out = ctx.createImageData(size, size)
  const data = out.data
  const strength = 4
  const sample = (x, y) => {
    const sx = (x + size) % size
    const sy = (y + size) % size
    return src[(sy * size + sx) * 4] / 255
  }

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const left = sample(x - 1, y)
      const right = sample(x + 1, y)
      const up = sample(x, y - 1)
      const down = sample(x, y + 1)

      const dx = (right - left) * strength
      const dy = (down - up) * strength
      const normal = new THREE.Vector3(-dx, -dy, 1).normalize()

      const i = (y * size + x) * 4
      data[i] = Math.round((normal.x * 0.5 + 0.5) * 255)
      data[i + 1] = Math.round((normal.y * 0.5 + 0.5) * 255)
      data[i + 2] = Math.round((normal.z * 0.5 + 0.5) * 255)
      data[i + 3] = 255
    }
  }

  ctx.putImageData(out, 0, 0)
  const tex = new THREE.CanvasTexture(canvas)
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.colorSpace = THREE.NoColorSpace
  tex.needsUpdate = true
  _canvasWeaveNormalBaseTexture = tex
  return tex
}

/** Inches per weave normal tile on gallery-wrapped stretched canvas (fabric wrap faces). */
export const GALLERY_WRAP_CANVAS_FABRIC_WEAVE_TILE_INCHES = 5
/** Acoustic fabric wrap (same geometry as gallery canvas): tighter weave than canvas; ~20% finer than earlier acoustic tuning. */
export const ACOUSTIC_PANEL_FABRIC_WEAVE_TILE_INCHES = 3.4

/** MeshStandardMaterial `normalScale` for weave on gallery-wrapped canvas fabric faces. */
export const GALLERY_WRAP_CANVAS_FABRIC_WEAVE_NORMAL_SCALE = 0.35
/** Acoustic: softer weave shading than canvas; ~20% lighter than earlier acoustic tuning. */
export const ACOUSTIC_PANEL_FABRIC_WEAVE_NORMAL_SCALE = 0.224

function weaveNormalUVs(widthInches, heightInches, tileInches = GALLERY_WRAP_CANVAS_FABRIC_WEAVE_TILE_INCHES) {
  const t = Math.max(Number(tileInches) || GALLERY_WRAP_CANVAS_FABRIC_WEAVE_TILE_INCHES, 1e-3)
  const rx = widthInches / t
  const ry = heightInches / t
  return {
    repeat: new THREE.Vector2(rx, ry),
    offset: new THREE.Vector2(-((rx * 0.5) % 1), -((ry * 0.5) % 1)),
  }
}

function createCanvasBackMaterial(
  widthInches,
  heightInches,
  fabricWeaveTileInches = GALLERY_WRAP_CANVAS_FABRIC_WEAVE_TILE_INCHES,
  fabricWeaveNormalScale = GALLERY_WRAP_CANVAS_FABRIC_WEAVE_NORMAL_SCALE,
) {
  const weaveNormalBase = getCanvasWeaveNormalBaseTexture()
  const { repeat, offset } = weaveNormalUVs(widthInches, heightInches, fabricWeaveTileInches)
  const normalMap = weaveNormalBase ? createRepeatedCanvasTexture(weaveNormalBase, repeat, offset) : null
  const ns = Number(fabricWeaveNormalScale) || GALLERY_WRAP_CANVAS_FABRIC_WEAVE_NORMAL_SCALE
  return new THREE.MeshStandardMaterial({
    color: 0xf5f0e8,
    normalMap,
    normalScale: new THREE.Vector2(ns, ns),
    roughness: 0.97,
    metalness: 0,
    side: THREE.FrontSide,
  })
}

/** Matte black for acoustic fabric-wrap side faces (depth edges); one instance per mesh for clean dispose. */
function createAcousticWrapBlackEdgeMaterial() {
  return new THREE.MeshStandardMaterial({
    color: 0x080808,
    roughness: 0.94,
    metalness: 0.02,
    side: THREE.FrontSide,
  })
}

/** Flat back for acoustic: no fabric weave — reads as plain particle board / cardboard. */
function createAcousticParticleBoardBackMaterial() {
  return new THREE.MeshStandardMaterial({
    color: 0xc4b5a0,
    roughness: 0.92,
    metalness: 0,
    side: THREE.FrontSide,
  })
}

function createCanvasWrapMaterial(
  baseTexture,
  repeat,
  offset,
  faceWidthInches,
  faceHeightInches,
  fabricWeaveTileInches = GALLERY_WRAP_CANVAS_FABRIC_WEAVE_TILE_INCHES,
  fabricWeaveNormalScale = GALLERY_WRAP_CANVAS_FABRIC_WEAVE_NORMAL_SCALE,
) {
  const weaveNormalBase = getCanvasWeaveNormalBaseTexture()
  const { repeat: normalRepeat, offset: normalOffset } = weaveNormalUVs(faceWidthInches, faceHeightInches, fabricWeaveTileInches)
  const normalMap = weaveNormalBase ? createRepeatedCanvasTexture(weaveNormalBase, normalRepeat, normalOffset) : null
  const ns = Number(fabricWeaveNormalScale) || GALLERY_WRAP_CANVAS_FABRIC_WEAVE_NORMAL_SCALE
  return new THREE.MeshStandardMaterial({
    map: createCanvasTextureSlice(baseTexture, repeat, offset),
    color: 0xffffff,
    normalMap,
    normalScale: new THREE.Vector2(ns, ns),
    roughness: 0.97,
    metalness: 0,
    side: THREE.FrontSide,
  })
}

export function updateCanvasWrapArtwork(group, {
  artworkTexture,
  artworkWidth,
  artworkHeight,
  fullWidth,
  fullHeight,
  cropLeft,
  cropTop,
  scaleX = 1,
  scaleY = 1,
  rotation = 0,
} = {}) {
  const wrapCanvas = group?.userData?.wrapCanvas
  const wrapTexture = group?.userData?.wrapTexture
  const layout = group?.userData?.wrapLayout
  if (!wrapCanvas || !wrapTexture || !layout) return

  drawCanvasWrapTexture({
    canvas: wrapCanvas,
    layout,
    artworkTexture,
    artworkWidth,
    artworkHeight,
    fullWidth,
    fullHeight,
    cropLeft,
    cropTop,
    scaleX,
    scaleY,
    rotation,
  })
  wrapTexture.needsUpdate = true
  const wrapTextures = group.userData?.wrapTextures || []
  wrapTextures.forEach((tex) => {
    tex.needsUpdate = true
  })
}

export function updateCanvasWrapNormalMaps(group, artWidth, artHeight) {
  const fabricWeaveTileInches = group.userData?.fabricWeaveTileInches ?? GALLERY_WRAP_CANVAS_FABRIC_WEAVE_TILE_INCHES
  group.traverse(obj => {
    if (!obj.isMesh || !obj.userData.dimsFn || !obj.material?.normalMap) return
    const [w, h] = obj.userData.dimsFn(artWidth, artHeight)
    const { repeat, offset } = weaveNormalUVs(w, h, fabricWeaveTileInches)
    obj.material.normalMap.repeat.copy(repeat)
    obj.material.normalMap.offset.copy(offset)
  })
}

// Computes atlas UV repeat/offset for each canvas wrap face given current art dimensions.
// Each face's uvFn is a closure that captures depthInches and backReturnInches at build time.
function makeCanvasWrapUvFn(surface, d, r) {
  return (artW, artH) => {
    const e = d + r
    const tw = artW + 2 * e
    const th = artH + 2 * e
    const uf0 = e / tw, uf1 = (e + artW) / tw
    const vf0 = e / th, vf1 = (e + artH) / th
    const V2 = (x, y) => new THREE.Vector2(x, y)
    switch (surface) {
      case 'front':          return { repeat: V2(uf1-uf0, vf1-vf0), offset: V2(uf0, vf0) }
      case 'top-edge':       return { repeat: V2(uf1-uf0, d/th),    offset: V2(uf0, (e+artH)/th) }
      case 'bottom-edge':    return { repeat: V2(uf1-uf0, d/th),    offset: V2(uf0, r/th) }
      case 'left-edge':      return { repeat: V2(d/tw, vf1-vf0),    offset: V2(r/tw, vf0) }
      case 'right-edge':     return { repeat: V2(d/tw, vf1-vf0),    offset: V2((e+artW)/tw, vf0) }
      case 'top-return':     return { repeat: V2(uf0-uf1, -r/th),   offset: V2(uf1, 1) }
      case 'bottom-return':  return { repeat: V2(uf0-uf1, -r/th),   offset: V2(uf1, r/th) }
      case 'left-return':    return { repeat: V2(r/tw, (artH-2*r)/th), offset: V2(0, vf0+r/th) }
      case 'right-return':   return { repeat: V2(r/tw, (artH-2*r)/th), offset: V2((r+2*d+artW)/tw, vf0+r/th) }
      default:               return null
    }
  }
}

export function updateCanvasWrapArtworkUVs(group, artWidth, artHeight) {
  group.traverse(obj => {
    if (!obj.isMesh || !obj.userData.uvFn || !obj.material?.map) return
    const uv = obj.userData.uvFn(artWidth, artHeight)
    if (!uv) return
    obj.material.map.repeat.copy(uv.repeat)
    obj.material.map.offset.copy(uv.offset)
  })
}

export function createCanvasWrapMesh(artWidth, artHeight, depthInches, artworkTexture, opts = {}) {
  const {
    fabricWeaveTileInches = GALLERY_WRAP_CANVAS_FABRIC_WEAVE_TILE_INCHES,
    fabricWeaveNormalScale = GALLERY_WRAP_CANVAS_FABRIC_WEAVE_NORMAL_SCALE,
    /** When true (acoustic), omit the additive front bevel ring — it reads as a heavy frame from the front. */
    suppressFabricFrontBevel = false,
    /** When true (acoustic), paint the four depth-wrap edge faces solid black (not printed fabric). */
    blackWrapSideEdges = false,
    /** When true (acoustic, 1-sided), omit canvas-style returns and use one flat particle-board back. */
    plainAcousticBack = false,
    /** When true (acoustic, 2-sided changeable), rear face uses same print as front; black edges between. */
    acousticDoubleSidedArtwork = false,
    ...textureOpts
  } = opts

  const group = new THREE.Group()
  group.userData = { type: 'canvas-wrap', fabricWeaveTileInches, fabricWeaveNormalScale }

  const depth = Math.max(inches(depthInches), inches(0.01))
  const backReturnInches = getCanvasBackReturnInches(depthInches)
  const returnDepth = inches(backReturnInches)
  const aw = inches(artWidth)
  const ah = inches(artHeight)

  const layout = getCanvasWrapTextureLayout(artWidth, artHeight, depthInches, backReturnInches)
  const wrapCanvas = document.createElement('canvas')
  wrapCanvas.width = layout.width
  wrapCanvas.height = layout.height

  drawCanvasWrapTexture({
    canvas: wrapCanvas,
    layout,
    artworkTexture,
    artworkWidth: artWidth,
    artworkHeight: artHeight,
    fullWidth: textureOpts.fullWidth,
    fullHeight: textureOpts.fullHeight,
    cropLeft: textureOpts.cropLeft,
    cropTop: textureOpts.cropTop,
    scaleX: textureOpts.scaleX,
    scaleY: textureOpts.scaleY,
    rotation: textureOpts.rotation,
  })

  const wrapTexture = new THREE.CanvasTexture(wrapCanvas)
  wrapTexture.colorSpace = THREE.SRGBColorSpace
  wrapTexture.wrapS = THREE.ClampToEdgeWrapping
  wrapTexture.wrapT = THREE.ClampToEdgeWrapping
  wrapTexture.needsUpdate = true

  const totalW = artWidth + 2 * (depthInches + backReturnInches)
  const totalH = artHeight + 2 * (depthInches + backReturnInches)
  const uFront0 = (depthInches + backReturnInches) / totalW
  const uFront1 = (depthInches + backReturnInches + artWidth) / totalW
  const vFront0 = (depthInches + backReturnInches) / totalH
  const vFront1 = (depthInches + backReturnInches + artHeight) / totalH
  const uSideLeft0 = backReturnInches / totalW
  const uSideLeft1 = (backReturnInches + depthInches) / totalW
  const uSideRight0 = (backReturnInches + depthInches + artWidth) / totalW
  const uSideRight1 = (backReturnInches + 2 * depthInches + artWidth) / totalW
  const vSideBottom0 = backReturnInches / totalH
  const vSideBottom1 = (backReturnInches + depthInches) / totalH
  const vSideTop0 = (backReturnInches + depthInches + artHeight) / totalH
  const vSideTop1 = (backReturnInches + 2 * depthInches + artHeight) / totalH
  const uBackLeft0 = 0
  const uBackLeft1 = backReturnInches / totalW
  const uBackRight0 = (backReturnInches + 2 * depthInches + artWidth) / totalW
  const uBackRight1 = 1
  const vBackBottom0 = 0
  const vBackBottom1 = backReturnInches / totalH
  const vBackTop0 = (backReturnInches + 2 * depthInches + artHeight) / totalH
  const vBackTop1 = 1

  const wrapTextures = []
  const pushTexture = (material) => {
    if (material?.map) wrapTextures.push(material.map)
    return material
  }
  const makeFace = (geometry, material, position, rotation, userData = {}) => {
    const mesh = new THREE.Mesh(geometry, pushTexture(material))
    mesh.position.copy(position)
    mesh.rotation.set(rotation.x, rotation.y, rotation.z)
    mesh.userData = {
      type: 'artwork',
      label: 'Artwork',
      widthInches: artWidth,
      heightInches: artHeight,
      integratedArtwork: true,
      ...userData,
    }
    group.add(mesh)
  }

  makeFace(
    new THREE.PlaneGeometry(aw, ah),
    createCanvasWrapMaterial(
      wrapTexture,
      new THREE.Vector2(uFront1 - uFront0, vFront1 - vFront0),
      new THREE.Vector2(uFront0, vFront0),
      artWidth,
      artHeight,
      fabricWeaveTileInches,
      fabricWeaveNormalScale,
    ),
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(0, 0, 0),
    { surface: 'front', dimsFn: (w, h) => [w, h], uvFn: makeCanvasWrapUvFn('front', depthInches, backReturnInches) }
  )
  makeFace(
    new THREE.PlaneGeometry(aw, depth),
    blackWrapSideEdges
      ? createAcousticWrapBlackEdgeMaterial()
      : createCanvasWrapMaterial(
        wrapTexture,
        new THREE.Vector2(uFront1 - uFront0, vSideTop1 - vSideTop0),
        new THREE.Vector2(uFront0, vSideTop0),
        artWidth,
        depthInches,
        fabricWeaveTileInches,
        fabricWeaveNormalScale,
      ),
    new THREE.Vector3(0, ah / 2, -depth / 2),
    new THREE.Vector3(-Math.PI / 2, 0, 0),
    { surface: 'top-edge', dimsFn: (w) => [w, depthInches], uvFn: makeCanvasWrapUvFn('top-edge', depthInches, backReturnInches) }
  )
  makeFace(
    new THREE.PlaneGeometry(aw, depth),
    blackWrapSideEdges
      ? createAcousticWrapBlackEdgeMaterial()
      : createCanvasWrapMaterial(
        wrapTexture,
        new THREE.Vector2(uFront1 - uFront0, vSideBottom1 - vSideBottom0),
        new THREE.Vector2(uFront0, vSideBottom0),
        artWidth,
        depthInches,
        fabricWeaveTileInches,
        fabricWeaveNormalScale,
      ),
    new THREE.Vector3(0, -ah / 2, -depth / 2),
    new THREE.Vector3(Math.PI / 2, 0, 0),
    { surface: 'bottom-edge', dimsFn: (w) => [w, depthInches], uvFn: makeCanvasWrapUvFn('bottom-edge', depthInches, backReturnInches) }
  )
  makeFace(
    new THREE.PlaneGeometry(depth, ah),
    blackWrapSideEdges
      ? createAcousticWrapBlackEdgeMaterial()
      : createCanvasWrapMaterial(
        wrapTexture,
        new THREE.Vector2(uSideLeft1 - uSideLeft0, vFront1 - vFront0),
        new THREE.Vector2(uSideLeft0, vFront0),
        depthInches,
        artHeight,
        fabricWeaveTileInches,
        fabricWeaveNormalScale,
      ),
    new THREE.Vector3(-aw / 2, 0, -depth / 2),
    new THREE.Vector3(0, -Math.PI / 2, 0),
    { surface: 'left-edge', dimsFn: (w, h) => [depthInches, h], uvFn: makeCanvasWrapUvFn('left-edge', depthInches, backReturnInches) }
  )
  makeFace(
    new THREE.PlaneGeometry(depth, ah),
    blackWrapSideEdges
      ? createAcousticWrapBlackEdgeMaterial()
      : createCanvasWrapMaterial(
        wrapTexture,
        new THREE.Vector2(uSideRight1 - uSideRight0, vFront1 - vFront0),
        new THREE.Vector2(uSideRight0, vFront0),
        depthInches,
        artHeight,
        fabricWeaveTileInches,
        fabricWeaveNormalScale,
      ),
    new THREE.Vector3(aw / 2, 0, -depth / 2),
    new THREE.Vector3(0, Math.PI / 2, 0),
    { surface: 'right-edge', dimsFn: (w, h) => [depthInches, h], uvFn: makeCanvasWrapUvFn('right-edge', depthInches, backReturnInches) }
  )

  const omitCanvasStyleReturns = plainAcousticBack || acousticDoubleSidedArtwork
  if (!omitCanvasStyleReturns) {
    const backStripH = Math.max(ah - 2 * returnDepth, 0.1)
    makeFace(
      new THREE.PlaneGeometry(aw, returnDepth),
      createCanvasWrapMaterial(
        wrapTexture,
        new THREE.Vector2(uFront0 - uFront1, vBackTop0 - vBackTop1),
        new THREE.Vector2(uFront1, vBackTop1),
        artWidth,
        backReturnInches,
        fabricWeaveTileInches,
        fabricWeaveNormalScale,
      ),
      new THREE.Vector3(0, ah / 2 - returnDepth / 2, -depth),
      new THREE.Vector3(0, Math.PI, 0),
      { surface: 'top-return', dimsFn: (w) => [w, backReturnInches], uvFn: makeCanvasWrapUvFn('top-return', depthInches, backReturnInches) }
    )
    makeFace(
      new THREE.PlaneGeometry(aw, returnDepth),
      createCanvasWrapMaterial(
        wrapTexture,
        new THREE.Vector2(uFront0 - uFront1, vBackBottom0 - vBackBottom1),
        new THREE.Vector2(uFront1, vBackBottom1),
        artWidth,
        backReturnInches,
        fabricWeaveTileInches,
        fabricWeaveNormalScale,
      ),
      new THREE.Vector3(0, -ah / 2 + returnDepth / 2, -depth),
      new THREE.Vector3(0, Math.PI, 0),
      { surface: 'bottom-return', dimsFn: (w) => [w, backReturnInches], uvFn: makeCanvasWrapUvFn('bottom-return', depthInches, backReturnInches) }
    )
    makeFace(
      new THREE.PlaneGeometry(returnDepth, backStripH),
      createCanvasWrapMaterial(
        wrapTexture,
        new THREE.Vector2(uBackLeft1 - uBackLeft0, vFront1 - vFront0 - (2 * returnDepth / Math.max(totalH, 1e-6))),
        new THREE.Vector2(uBackLeft0, vFront0 + (returnDepth / Math.max(totalH, 1e-6))),
        backReturnInches,
        artHeight - 2 * backReturnInches,
        fabricWeaveTileInches,
        fabricWeaveNormalScale,
      ),
      new THREE.Vector3(-aw / 2 + returnDepth / 2, 0, -depth),
      new THREE.Vector3(0, Math.PI, 0),
      { surface: 'left-return', dimsFn: (w, h) => [backReturnInches, h - 2 * backReturnInches], uvFn: makeCanvasWrapUvFn('left-return', depthInches, backReturnInches) }
    )
    makeFace(
      new THREE.PlaneGeometry(returnDepth, backStripH),
      createCanvasWrapMaterial(
        wrapTexture,
        new THREE.Vector2(uBackRight1 - uBackRight0, vFront1 - vFront0 - (2 * returnDepth / Math.max(totalH, 1e-6))),
        new THREE.Vector2(uBackRight0, vFront0 + (returnDepth / Math.max(totalH, 1e-6))),
        backReturnInches,
        artHeight - 2 * backReturnInches,
        fabricWeaveTileInches,
        fabricWeaveNormalScale,
      ),
      new THREE.Vector3(aw / 2 - returnDepth / 2, 0, -depth),
      new THREE.Vector3(0, Math.PI, 0),
      { surface: 'right-return', dimsFn: (w, h) => [backReturnInches, h - 2 * backReturnInches], uvFn: makeCanvasWrapUvFn('right-return', depthInches, backReturnInches) }
    )
  }

  if (acousticDoubleSidedArtwork) {
    const backZ = -depth + inches(0.02)
    makeFace(
      new THREE.PlaneGeometry(aw, ah),
      createCanvasWrapMaterial(
        wrapTexture,
        new THREE.Vector2(uFront1 - uFront0, vFront1 - vFront0),
        new THREE.Vector2(uFront0, vFront0),
        artWidth,
        artHeight,
        fabricWeaveTileInches,
        fabricWeaveNormalScale,
      ),
      new THREE.Vector3(0, 0, backZ),
      new THREE.Vector3(0, Math.PI, 0),
      {
        surface: 'back-print',
        label: 'Artwork',
        dimsFn: (w, h) => [w, h],
        uvFn: makeCanvasWrapUvFn('front', depthInches, backReturnInches),
      }
    )
  } else if (plainAcousticBack) {
    const backZ = -depth + inches(0.02)
    makeFace(
      new THREE.PlaneGeometry(aw, ah),
      createAcousticParticleBoardBackMaterial(),
      new THREE.Vector3(0, 0, backZ),
      new THREE.Vector3(0, Math.PI, 0),
      { surface: 'back', label: 'Acoustic back' },
    )
  } else {
    makeFace(
      new THREE.PlaneGeometry(aw, ah),
      createCanvasBackMaterial(artWidth, artHeight, fabricWeaveTileInches, fabricWeaveNormalScale),
      new THREE.Vector3(0, 0, -inches(0.05)),
      new THREE.Vector3(0, Math.PI, 0),
      { surface: 'back', dimsFn: (w, h) => [w, h] }
    )
  }

  if (!suppressFabricFrontBevel) {
    const bevelRim = createPanelBevelRim(artWidth, artHeight, {
      useCanvasNormal: true,
      fabricWrapWeaveTileInches: fabricWeaveTileInches,
      fabricWeaveNormalScale,
    })
    if (bevelRim) {
      bevelRim.userData = { ...bevelRim.userData, surface: 'front-bevel' }
      group.add(bevelRim)
    }
  }

  group.userData.wrapCanvas = wrapCanvas
  group.userData.wrapTexture = wrapTexture
  group.userData.wrapTextures = wrapTextures
  group.userData.wrapLayout = layout
  group.userData.artworkTexture = artworkTexture
  group.userData.depthInches = depthInches
  group.userData.backReturnInches = backReturnInches
  group.userData.integratedArtwork = true

  return group
}

export function updateCanvasWrapGeometry(group, artWidth, artHeight) {
  if (!group) return
  const depthInches = Number(group.userData?.depthInches) || 0
  const backReturnInches = Number(group.userData?.backReturnInches ?? getCanvasBackReturnInches(depthInches))
  const nextLayout = getCanvasWrapTextureLayout(artWidth, artHeight, depthInches, backReturnInches)
  const wrapCanvas = group.userData?.wrapCanvas
  let canvasResized = false
  if (wrapCanvas) {
    if (wrapCanvas.width !== nextLayout.width) {
      wrapCanvas.width = nextLayout.width
      canvasResized = true
    }
    if (wrapCanvas.height !== nextLayout.height) {
      wrapCanvas.height = nextLayout.height
      canvasResized = true
    }
    group.userData.wrapLayout = nextLayout
  }
  if (canvasResized) {
    group.userData?.wrapTexture?.dispose?.()
    const wrapTextures = group.userData?.wrapTextures || []
    wrapTextures.forEach(tex => tex?.dispose?.())
  }
  const aw = inches(artWidth)
  const ah = inches(artHeight)
  const depth = Math.max(inches(depthInches), inches(0.01))
  const returnDepth = inches(backReturnInches)
  const backStripH = Math.max(ah - 2 * returnDepth, 0.1)

  const resetPlane = (obj, w, h, x, y, z) => {
    obj.geometry?.dispose?.()
    obj.geometry = new THREE.PlaneGeometry(Math.max(w, 0.1), Math.max(h, 0.1))
    obj.position.set(x, y, z)
    obj.scale.set(1, 1, 1)
  }

  group.traverse((obj) => {
    if (!obj.isMesh) return
    if (obj.userData?.type === 'panel-bevel-rim') {
      const newGeo = buildPanelBevelGeometry(artWidth, artHeight, {
        sizeInches: obj.userData.sizeInchesOverride != null ? obj.userData.sizeInchesOverride : undefined,
      })
      if (newGeo) {
        obj.geometry?.dispose?.()
        obj.geometry = newGeo
        obj.userData.panelWidth = artWidth
        obj.userData.panelHeight = artHeight
      }
      obj.scale.set(1, 1, 1)
      obj.position.set(0, 0, inches(panelBevelSettings.zOffsetInches))
      return
    }
    if (obj.userData?.type !== 'artwork') return
    switch (obj.userData.surface) {
      case 'front':
      case 'back-print':
        resetPlane(obj, aw, ah, 0, 0, obj.position.z)
        break
      case 'top-edge':
        resetPlane(obj, aw, depth, 0, ah / 2, -depth / 2)
        break
      case 'bottom-edge':
        resetPlane(obj, aw, depth, 0, -ah / 2, -depth / 2)
        break
      case 'left-edge':
        resetPlane(obj, depth, ah, -aw / 2, 0, -depth / 2)
        break
      case 'right-edge':
        resetPlane(obj, depth, ah, aw / 2, 0, -depth / 2)
        break
      case 'top-return':
        resetPlane(obj, aw, returnDepth, 0, ah / 2 - returnDepth / 2, -depth)
        break
      case 'bottom-return':
        resetPlane(obj, aw, returnDepth, 0, -ah / 2 + returnDepth / 2, -depth)
        break
      case 'left-return':
        resetPlane(obj, returnDepth, backStripH, -aw / 2 + returnDepth / 2, 0, -depth)
        break
      case 'right-return':
        resetPlane(obj, returnDepth, backStripH, aw / 2 - returnDepth / 2, 0, -depth)
        break
      case 'back': {
        const isPlainAcousticBack = !obj.userData.dimsFn
        if (isPlainAcousticBack) {
          resetPlane(obj, aw + 2 * depth, ah + 2 * depth, 0, 0, -depth + inches(0.02))
        } else {
          resetPlane(obj, aw, ah, 0, 0, -inches(0.05))
        }
        break
      }
      default:
        break
    }
  })
}

/**
 * Create a single mitered frame rail. When unitSize is true, geometry is 1×1×1
 * so the mesh can be scaled at runtime without rebuilding (resize-friendly).
 * For float frames: L profile that wraps to back, inside rabbet same color as front.
 *
 * @param {object} opts
 * @param {number} opts.innerLen   - inner edge length (toward artwork)
 * @param {number} opts.frameWidth - visible frame width in inches
 * @param {number} opts.depth      - frame depth in inches
 * @param {object} opts.textures   - { front, side, top } THREE.Texture objects
 * @param {'top'|'bottom'|'left'|'right'} opts.position - which edge (for UV orientation)
 * @param {boolean} opts.unitSize  - if true, create 1×1×1 geometry (caller sets mesh.scale)
 * @param {boolean} opts.isFloatFrame - if true, L profile with inside same color as front
 * @param {number|string|null} opts.frameColor - solid fallback color for untextured frame faces
 * @returns {THREE.Mesh}
 */
export function createMiteredRail({
  innerLen,
  frameWidth,
  depth,
  textures,
  unitSize = false,
  isFloatFrame = false,
  frameColor = null,
}) {
  const inner = unitSize ? 1 : inches(innerLen)
  const fw = unitSize ? 1 : inches(frameWidth)
  const d = unitSize ? 1 : inches(depth)

  const shape = new THREE.Shape()
  if (isFloatFrame) {
    // Simple trapezoid for float frames; back legs are separate meshes (avoids z-fighting)
    shape.moveTo(-inner / 2, 0)
    shape.lineTo(inner / 2, 0)
    shape.lineTo(inner / 2 + fw, fw)
    shape.lineTo(-inner / 2 - fw, fw)
    shape.closePath()
  } else {
    shape.moveTo(-inner / 2, 0)
    shape.lineTo(inner / 2, 0)
    shape.lineTo(inner / 2 + fw, fw)
    shape.lineTo(-inner / 2 - fw, fw)
    shape.closePath()
  }

  const extrudeSettings = {
    depth: d,
    bevelEnabled: false,
    material: 0,
    extrudeMaterial: 1,
    UVGenerator: createRailUVGenerator(inner, fw),
  }

  const geo = new THREE.ExtrudeGeometry(shape, extrudeSettings)
  geo.translate(0, 0, -d / 2) // center extrusion in z so all rails share the same z range

  const frontTex = textures.top ? textures.top.clone() : null
  if (frontTex) {
    if (!unitSize) {
      const railLen = inner + 2 * fw
      const aspect = frontTex.image ? frontTex.image.width / frontTex.image.height : 1
      const tiles = (railLen / fw) / aspect
      // U = along length (tile), V = cross (1:1 with rail width); UV gen puts texture top at outer
      // MirroredRepeatWrapping on S (length) for flip-flop pattern at mitered corners
      // Offset centers texture: u' = repeat*u + offset, so at u=0.5 we want u'≡0.5 → offset = 0.5*(1-tiles)
      const offsetX = tiles > 0 ? 0.5 * (1 - tiles) : 0
      frontTex.repeat.set(tiles, 1)
      frontTex.offset.set(offsetX, 0)
      rotateFrameTopTexture(frontTex)
      frontTex.updateMatrix()
      frontTex.wrapS = THREE.MirroredRepeatWrapping
      frontTex.wrapT = THREE.RepeatWrapping
    }
  }

  const palette = getFrameColorPalette(frameColor ?? textures.sampledColor)
  const frontMat = new THREE.MeshStandardMaterial({
    map: frontTex,
    color: frontTex ? 0xffffff : palette.front,
    side: isFloatFrame ? THREE.DoubleSide : THREE.FrontSide,
  })
  const sideMat = new THREE.MeshStandardMaterial({
    color: palette.side,
  })

  // Float frame: inside rabbet same color as front
  const mesh = new THREE.Mesh(geo, isFloatFrame ? frontMat : [frontMat, sideMat])
  mesh.castShadow = true
  mesh.receiveShadow = true

  if (unitSize) {
    mesh.scale.set(inches(innerLen), inches(frameWidth), inches(depth))
    if (frontTex) {
      const railLen = innerLen + 2 * frameWidth
      const aspect = frontTex.image ? frontTex.image.width / frontTex.image.height : 1
      const tiles = (railLen / frameWidth) / aspect
      const offsetX = tiles > 0 ? 0.5 * (1 - tiles) : 0
      frontTex.repeat.set(tiles, 1)
      frontTex.offset.set(offsetX, 0)
      rotateFrameTopTexture(frontTex)
      frontTex.updateMatrix()
      frontTex.wrapS = THREE.MirroredRepeatWrapping
      frontTex.wrapT = THREE.RepeatWrapping
    }
  }

  return mesh
}

/**
 * Replace a rail mesh's geometry with one built for new dimensions.
 * Reuses existing materials to avoid texture/material allocation.
 * @param {'top'|'bottom'|'left'|'right'} position - for left/right, geometry is rotated to align with vertical
 * @param {boolean} isFloatFrame - if true, use L profile shape
 */
export function updateMiteredRailGeometry(mesh, innerLen, frameWidth, position, depth = 1.5, isFloatFrame = false) {
  if (!Number.isFinite(innerLen) || !Number.isFinite(frameWidth) || !Number.isFinite(depth) || frameWidth <= 0 || depth <= 0) return
  const inner = inches(innerLen)
  const fw = inches(frameWidth)
  const d = inches(depth)

  const shape = new THREE.Shape()
  shape.moveTo(-inner / 2, 0)
  shape.lineTo(inner / 2, 0)
  shape.lineTo(inner / 2 + fw, fw)
  shape.lineTo(-inner / 2 - fw, fw)
  shape.closePath()

  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: d,
    bevelEnabled: false,
    material: 0,
    extrudeMaterial: 1,
    UVGenerator: createRailUVGenerator(inner, fw),
  })
  geo.translate(0, 0, -d / 2)

  if (position === 'left' || position === 'right') {
    geo.rotateX(Math.PI)
  }

  if (mesh.geometry) mesh.geometry.dispose()
  mesh.geometry = geo
  mesh.scale.set(1, 1, 1)

  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
  const frontMat = materials[0]
  const frontTex = frontMat?.map
  if (frontTex && frontTex.image) {
    const railLen = innerLen + 2 * frameWidth
    const aspect = frontTex.image.width / frontTex.image.height
    const tiles = frameWidth > 0 && aspect > 0 ? (railLen / frameWidth) / aspect : 1
    const offsetX = tiles > 0 ? 0.5 * (1 - tiles) : 0
    frontTex.repeat.set(tiles, 1)
    frontTex.offset.set(offsetX, 0)
    rotateFrameTopTexture(frontTex)
    frontTex.updateMatrix()
    frontTex.wrapS = THREE.MirroredRepeatWrapping
    frontTex.wrapT = THREE.RepeatWrapping
  }

  if (!isFloatFrame && materials[1]) {
    materials[1].needsUpdate = true
  }
}

/**
 * Build a complete mitered frame as a THREE.Group.
 * Each rail has 45° mitered ends so front faces meet at corners.
 *
 * @param {object} opts
 * @param {number} opts.artWidth   - inner opening width in inches
 * @param {number} opts.artHeight  - inner opening height in inches
 * @param {number} opts.frameWidth - frame moulding width in inches
 * @param {number} opts.rabbet     - rabbet depth in inches
 * @param {number} opts.depth      - total frame height/depth in inches
 * @param {object} opts.textures   - { front, side, top } from API
 * @returns {THREE.Group}
 */
export function createMiteredFrame({
  artWidth,
  artHeight,
  frameWidth,
  rabbet = 0, // eslint-disable-line no-unused-vars
  depth = 1.5,
  textures = {},
  unitSize = false,
  isFloatFrame = false,
  frameColor = null,
}) {
  const group = new THREE.Group()
  group.userData = { type: 'frame' }

  const aw = inches(artWidth)
  const ah = inches(artHeight)
  const d = inches(depth)
  const fw = inches(frameWidth)

  // Top rail: inner length = aw, positioned above opening
  const topRail = createMiteredRail({
    innerLen: artWidth,
    frameWidth,
    depth,
    textures,
    position: 'top',
    unitSize,
    isFloatFrame,
    frameColor,
  })
  topRail.position.set(0, ah / 2, d / 2)
  topRail.userData = { type: 'frame-rail', position: 'top', widthInches: frameWidth }
  group.add(topRail)

  // Bottom rail: mirror in Y, inner length = aw
  const bottomRail = createMiteredRail({
    innerLen: artWidth,
    frameWidth,
    depth,
    textures,
    position: 'bottom',
    unitSize,
    isFloatFrame,
    frameColor,
  })
  bottomRail.scale.y = -1
  bottomRail.position.set(0, -ah / 2, d / 2)
  bottomRail.userData = { type: 'frame-rail', position: 'bottom', widthInches: frameWidth }
  group.add(bottomRail)

  // Left rail: inner length = ah, rotated 90°; flip geometry so front faces viewer
  const leftRail = createMiteredRail({
    innerLen: artHeight,
    frameWidth,
    depth,
    textures,
    position: 'left',
    unitSize,
    isFloatFrame,
    frameColor,
  })
  leftRail.geometry.rotateX(Math.PI)
  leftRail.rotation.z = -Math.PI / 2
  leftRail.position.set(-aw / 2, 0, d / 2)
  leftRail.userData = { type: 'frame-rail', position: 'left', widthInches: frameWidth }
  group.add(leftRail)

  // Right rail: inner length = ah, rotated 90°; flip geometry so front faces viewer
  const rightRail = createMiteredRail({
    innerLen: artHeight,
    frameWidth,
    depth,
    textures,
    position: 'right',
    unitSize,
    isFloatFrame,
    frameColor,
  })
  rightRail.geometry.rotateX(Math.PI)
  rightRail.rotation.z = Math.PI / 2
  rightRail.position.set(aw / 2, 0, d / 2)
  rightRail.userData = { type: 'frame-rail', position: 'right', widthInches: frameWidth }
  group.add(rightRail)

  if (isFloatFrame) {
    // Back legs: same color as frame, only as large as frame, extend inward from outer edge
    // Outer edge = frame outer (±(aw/2 + fw)); legs extend inward by backLegThick, mitered at corners
    const backLegDepth = inches(0.25)
    const backLegThick = inches(1.5)
    const m = backLegThick / Math.SQRT2
    const outerTop = ah / 2 + fw
    const outerBottom = -ah / 2 - fw
    const outerLeft = -aw / 2 - fw
    const outerRight = aw / 2 + fw
    const makeBackMat = (railLen) => {
      const frontTex = textures.top ? textures.top.clone() : null
      const palette = getFrameColorPalette(frameColor ?? textures.sampledColor)
      const texAspect = frontTex?.image ? frontTex.image.width / frontTex.image.height : 1
      const tiles = texAspect > 0 ? (railLen / fw) / texAspect : 1
      const offsetX = tiles > 0 ? 0.5 * (1 - tiles) : 0
      const mat = new THREE.MeshStandardMaterial({
        map: frontTex,
        color: frontTex ? 0xffffff : palette.front,
        side: THREE.DoubleSide,
      })
      if (frontTex) {
        frontTex.repeat.set(tiles, 1)
        frontTex.offset.set(offsetX, 0)
        frontTex.wrapS = THREE.MirroredRepeatWrapping
        frontTex.wrapT = THREE.RepeatWrapping
        rotateFrameTopTexture(frontTex)
        frontTex.updateMatrix()
      }
      return mat
    }
    const topBottomLen = aw + 2 * fw
    const leftRightLen = ah + 2 * fw

    const extrudeOpts = (pos) => ({
      depth: backLegDepth,
      bevelEnabled: false,
      UVGenerator: createBackLegUVGenerator(outerLeft, outerRight, outerBottom, outerTop, fw, m, pos),
    })

    const topShape = new THREE.Shape()
    topShape.moveTo(outerLeft, outerTop)
    topShape.lineTo(outerRight, outerTop)
    topShape.lineTo(outerRight - m, outerTop - m)
    topShape.lineTo(outerLeft + m, outerTop - m)
    topShape.closePath()
    const zOffset = -backLegDepth / 2 - inches(0.02)
    const topGeo = new THREE.ExtrudeGeometry(topShape, extrudeOpts('top'))
    topGeo.translate(0, 0, -backLegDepth / 2)
    topGeo.scale(1, 1, -1)
    const topBack = new THREE.Mesh(topGeo, makeBackMat(topBottomLen))
    topBack.position.set(0, 0, zOffset)
    topBack.userData = { type: 'frame-back-leg', position: 'top' }
    group.add(topBack)

    const bottomShape = new THREE.Shape()
    bottomShape.moveTo(outerRight, outerBottom)
    bottomShape.lineTo(outerLeft, outerBottom)
    bottomShape.lineTo(outerLeft + m, outerBottom + m)
    bottomShape.lineTo(outerRight - m, outerBottom + m)
    bottomShape.closePath()
    const bottomGeo = new THREE.ExtrudeGeometry(bottomShape, extrudeOpts('bottom'))
    bottomGeo.translate(0, 0, -backLegDepth / 2)
    bottomGeo.scale(1, 1, -1)
    const bottomBack = new THREE.Mesh(bottomGeo, makeBackMat(topBottomLen))
    bottomBack.position.set(0, 0, zOffset)
    bottomBack.userData = { type: 'frame-back-leg', position: 'bottom' }
    group.add(bottomBack)

    const leftShape = new THREE.Shape()
    leftShape.moveTo(outerLeft, outerBottom)
    leftShape.lineTo(outerLeft, outerTop)
    leftShape.lineTo(outerLeft + m, outerTop - m)
    leftShape.lineTo(outerLeft + m, outerBottom + m)
    leftShape.closePath()
    const leftGeo = new THREE.ExtrudeGeometry(leftShape, extrudeOpts('left'))
    leftGeo.translate(0, 0, -backLegDepth / 2)
    leftGeo.scale(1, 1, -1)
    const leftBack = new THREE.Mesh(leftGeo, makeBackMat(leftRightLen))
    leftBack.position.set(0, 0, zOffset)
    leftBack.userData = { type: 'frame-back-leg', position: 'left' }
    group.add(leftBack)

    const rightShape = new THREE.Shape()
    rightShape.moveTo(outerRight, outerTop)
    rightShape.lineTo(outerRight, outerBottom)
    rightShape.lineTo(outerRight - m, outerBottom + m)
    rightShape.lineTo(outerRight - m, outerTop - m)
    rightShape.closePath()
    const rightGeo = new THREE.ExtrudeGeometry(rightShape, extrudeOpts('right'))
    rightGeo.translate(0, 0, -backLegDepth / 2)
    rightGeo.scale(1, 1, -1)
    const rightBack = new THREE.Mesh(rightGeo, makeBackMat(leftRightLen))
    rightBack.position.set(0, 0, zOffset)
    rightBack.userData = { type: 'frame-back-leg', position: 'right' }
    group.add(rightBack)
  }

  return group
}

/**
 * Rebuild a float frame back leg mesh for new dimensions. Used on resize.
 * @param {THREE.Mesh} mesh - back leg mesh (has userData.position)
 * @param {number} innerWidth - frame opening width in inches
 * @param {number} innerHeight - frame opening height in inches
 * @param {number} frameWidth - frame width in inches
 * @param {number} frameDepth - frame depth in inches (unused; back legs use fixed depth)
 */
export function updateBackLegGeometry(mesh, innerWidth, innerHeight, frameWidth, frameDepth) { // eslint-disable-line no-unused-vars -- frameDepth reserved for future use
  if (!Number.isFinite(innerWidth) || !Number.isFinite(innerHeight) || !Number.isFinite(frameWidth) || frameWidth <= 0) return
  const aw = inches(innerWidth)
  const ah = inches(innerHeight)
  const fw = inches(frameWidth)
  const backLegDepth = inches(0.25)
  const backLegThick = inches(1.5)
  const m = backLegThick / Math.SQRT2
  const outerTop = ah / 2 + fw
  const outerBottom = -ah / 2 - fw
  const outerLeft = -aw / 2 - fw
  const outerRight = aw / 2 + fw
  const extrudeOpts = {
    depth: backLegDepth,
    bevelEnabled: false,
    UVGenerator: createBackLegUVGenerator(outerLeft, outerRight, outerBottom, outerTop, fw, m, mesh.userData?.position),
  }
  const pos = mesh.userData?.position

  let shape
  if (pos === 'top') {
    shape = new THREE.Shape()
    shape.moveTo(outerLeft, outerTop)
    shape.lineTo(outerRight, outerTop)
    shape.lineTo(outerRight - m, outerTop - m)
    shape.lineTo(outerLeft + m, outerTop - m)
    shape.closePath()
  } else if (pos === 'bottom') {
    shape = new THREE.Shape()
    shape.moveTo(outerRight, outerBottom)
    shape.lineTo(outerLeft, outerBottom)
    shape.lineTo(outerLeft + m, outerBottom + m)
    shape.lineTo(outerRight - m, outerBottom + m)
    shape.closePath()
  } else if (pos === 'left') {
    shape = new THREE.Shape()
    shape.moveTo(outerLeft, outerBottom)
    shape.lineTo(outerLeft, outerTop)
    shape.lineTo(outerLeft + m, outerTop - m)
    shape.lineTo(outerLeft + m, outerBottom + m)
    shape.closePath()
  } else if (pos === 'right') {
    shape = new THREE.Shape()
    shape.moveTo(outerRight, outerTop)
    shape.lineTo(outerRight, outerBottom)
    shape.lineTo(outerRight - m, outerBottom + m)
    shape.lineTo(outerRight - m, outerTop - m)
    shape.closePath()
  } else {
    return
  }

  const geo = new THREE.ExtrudeGeometry(shape, extrudeOpts)
  geo.translate(0, 0, -backLegDepth / 2)
  geo.scale(1, 1, -1)
  if (mesh.geometry) mesh.geometry.dispose()
  mesh.geometry = geo
  const zOffset = -backLegDepth / 2 - inches(0.02)
  mesh.position.set(0, 0, zOffset)

  const mat = mesh.material
  const tex = mat?.map
  if (tex && tex.image) {
    const isHorizontal = pos === 'top' || pos === 'bottom'
    const railLen = isHorizontal ? innerWidth + 2 * frameWidth : innerHeight + 2 * frameWidth
    const aspect = tex.image.width / tex.image.height
    const tiles = frameWidth > 0 && aspect > 0 ? (railLen / frameWidth) / aspect : 1
    const offsetX = tiles > 0 ? 0.5 * (1 - tiles) : 0
    tex.repeat.set(tiles, 1)
    tex.offset.set(offsetX, 0)
    tex.wrapS = THREE.MirroredRepeatWrapping
    tex.wrapT = THREE.RepeatWrapping
    rotateFrameTopTexture(tex)
    tex.updateMatrix()
  }
}

/**
 * Create hang hardware on the back of the frame.
 * Positioned at negative Z (back side). Styles: wire, security hardware, d-ring, sawtooth, cleat.
 * Backing paper only for Print substrate.
 * Hardware is placed at backmost level (behind stretchers or frame).
 *
 * @param {object} opts
 * @param {number} opts.innerWidth  - opening width (art + mat) in inches
 * @param {number} opts.innerHeight - opening height (art + mat) in inches
 * @param {string} opts.hangType    - hang option name, e.g. 'wire', 'security hardware', 'd-ring'
 * @param {string} opts.substrate   - substrate name (e.g. 'Print', 'Canvas') - backing only for Print
 * @param {number} opts.backDepth   - backmost depth in inches (stretcher or frame depth); hardware placed here
 * @param {number} opts.hardwareWidth - width used for frame-mounted hardware placement
 * @param {number} opts.hardwareHeight - height used for frame-mounted hardware placement
 * @param {number} opts.wireAnchorWidth - horizontal span between wire attachment points
 * @param {number|null} opts.securityVerticalInset - top/bottom edge inset for security hardware, in inches
 * @returns {THREE.Group}
 */
export function createHangHardware({
  innerWidth,
  innerHeight,
  hangType = '',
  substrate = '',
  backDepth = 0.6,
  hardwareWidth = innerWidth,
  hardwareHeight = innerHeight,
  wireAnchorWidth = null,
  securityVerticalInset = null,
}) {
  const group = new THREE.Group()
  group.userData = { type: 'hang-hardware' }

  const w = inches(innerWidth)
  const h = inches(innerHeight)
  const hw = inches(hardwareWidth)
  const hh = inches(hardwareHeight)
  const backZ = -inches(backDepth)
  const zOffset = inches(0.03) // small offset behind back face to avoid z-fighting
  const zBack = backZ - zOffset // sawtooth/cleat at back
  const zBacking = backZ + zOffset // backing paper sits just inside the frame back face
  const zWire = backZ - zOffset // hardware at back
  const metalColor = 0x8a8a8a
  const metalMat = new THREE.MeshStandardMaterial({
    color: metalColor,
    metalness: 0.7,
    roughness: 0.3,
  })

  const type = (hangType || '').toLowerCase()
  const isPrint = String(substrate || '').toLowerCase() === 'print'

  // Inset from edges so hardware stays within the selected hardware bounds.
  const inset = Math.max(inches(1), Math.min(hw, hh) * 0.08)
  const wireInset = Math.min(hw, hh) * 0.1
  const defaultWireSpan = Math.max(0.1, hw - (2 * wireInset))
  const wireSpan = wireAnchorWidth != null
    ? Math.max(0.1, Math.min(inches(wireAnchorWidth), hw))
    : defaultWireSpan
  const makeWire = () => {
    const wireRadius = inches(0.04)
    const segs = 24
    const points = []
    for (let i = 0; i <= segs; i++) {
      const t = i / segs
      const sag = 1 - 4 * (t - 0.5) ** 2
      const x = -wireSpan / 2 + t * wireSpan
      const y = (0.1 * hh) + (0.2 * hh * sag)
      points.push(new THREE.Vector3(x, y, zWire))
    }
    const curve = new THREE.CatmullRomCurve3(points)
    const tubeGeo = new THREE.TubeGeometry(curve, segs, wireRadius, 6)
    const wireMesh = new THREE.Mesh(tubeGeo, metalMat)
    wireMesh.renderOrder = 1 // draw on top of back panel
    group.add(wireMesh)
  }

  // Back panel (dust cover) - Print only; other substrates have no backing paper
  if (isPrint) {
    const backMat = new THREE.MeshStandardMaterial({
      color: 0xf4ecd8, // unbleached paper
      roughness: 0.9,
      metalness: 0,
      side: THREE.DoubleSide,
    })
    const backPanel = new THREE.Mesh(
      new THREE.PlaneGeometry(w, h),
      backMat
    )
    backPanel.position.set(0, 0, zBacking)
    backPanel.renderOrder = 0
    backPanel.userData = { type: 'back-panel' }
    group.add(backPanel)
  }

  if (type.includes('wire') || type.includes('cable') || type.includes('hook')) {
    makeWire()
  } else if (type.includes('security') || type.includes('d-ring') || type.includes('d ring')) {
    // Security hanging kit: 3 plates with capsule holes (2 top corners + 1 bottom center)
    const zHw = zWire
    const plateW = inches(2.25)
    const plateH = inches(0.85)
    const holeW = inches(0.6)
    const holeH = inches(0.25)
    const plateThick = inches(0.04)
    const plateGeo = createPlateWithCapsuleHole(plateW, plateH, holeW, holeH, plateThick)
    const securityInsetY = securityVerticalInset != null
      ? Math.max(0, Math.min(hh / 2, inches(securityVerticalInset) + (plateH / 2)))
      : inset
    const positions = [
      { x: -hw / 2 + inset, y: hh / 2 - securityInsetY },
      { x: hw / 2 - inset, y: hh / 2 - securityInsetY },
      { x: 0, y: -hh / 2 + securityInsetY },
    ]
    positions.forEach((pos) => {
      const plate = new THREE.Mesh(plateGeo, metalMat)
      plate.position.set(pos.x, pos.y, zHw)
      plate.renderOrder = 1
      group.add(plate)
    })
  } else if (type.includes('sawtooth')) {
    // Sawtooth hanger strip - thin bar with teeth
    const barW = hw * 0.6
    const barH = inches(0.1)
    const barGeo = new THREE.BoxGeometry(barW, barH, inches(0.05))
    const bar = new THREE.Mesh(barGeo, metalMat)
    bar.position.set(0, hh / 2 - inset, zBack)
    group.add(bar)
  } else if (type.includes('cleat')) {
    // Wall cleat - angled bar
    const cleatW = inches(2)
    const cleatH = inches(0.08)
    const cleatGeo = new THREE.BoxGeometry(cleatW, cleatH, inches(0.1))
    const cleat = new THREE.Mesh(cleatGeo, metalMat)
    cleat.position.set(0, hh / 2 - inset, zBack)
    cleat.rotation.x = Math.PI / 6
    group.add(cleat)
  } else {
    makeWire()
  }

  return group
}

/**
 * Create stretcher bar geometry for canvas substrate.
 * Four wooden rails at the canvas edges forming the inner support the canvas wraps over.
 *
 * @param {number} artWidth  - artwork/canvas width in inches
 * @param {number} artHeight - artwork/canvas height in inches
 * @param {number} depthInches - stretcher depth in inches (e.g. 2)
 * @returns {THREE.Group}
 */
export function createStretcherBars(artWidth, artHeight, depthInches, insetInches = 0.1) {
  const group = new THREE.Group()
  group.userData = { type: 'stretcher-bars' }

  const aw = inches(artWidth)
  const ah = inches(artHeight)
  const railThick = inches(depthInches)
  const inset = inches(insetInches)
  // Bar depth fits inside the canvas shell with inset on front and back faces
  const depth = Math.max(railThick - 2 * inset, 0.01)

  const woodMat = new THREE.MeshStandardMaterial({
    color: 0x8b7355,
    roughness: 0.8,
    metalness: 0,
  })
  const innerAw = Math.max(aw - 2 * inset, 0.1)
  const innerAh = Math.max(ah - 2 * inset, 0.1)

  // Keep the stretcher assembly just inside the canvas shell in all dimensions.
  const z = -(depth / 2 + inset)

  const renderOrderStretcher = 0 // draw first so artwork overwrites

  // Top rail - inset within image bounds
  const topGeo = new THREE.BoxGeometry(innerAw, railThick, depth)
  const topMesh = new THREE.Mesh(topGeo, woodMat)
  topMesh.position.set(0, innerAh / 2 - railThick / 2, z)
  topMesh.userData = { type: 'stretcher-rail', side: 'top' }
  topMesh.renderOrder = renderOrderStretcher
  group.add(topMesh)

  // Bottom rail
  const bottomGeo = new THREE.BoxGeometry(innerAw, railThick, depth)
  const bottomMesh = new THREE.Mesh(bottomGeo, woodMat)
  bottomMesh.position.set(0, -innerAh / 2 + railThick / 2, z)
  bottomMesh.userData = { type: 'stretcher-rail', side: 'bottom' }
  bottomMesh.renderOrder = renderOrderStretcher
  group.add(bottomMesh)

  // Left rail - height excludes top/bottom rails
  const sideRailH = Math.max(innerAh - 2 * railThick, 0.1)
  const leftGeo = new THREE.BoxGeometry(railThick, sideRailH, depth)
  const leftMesh = new THREE.Mesh(leftGeo, woodMat)
  leftMesh.position.set(-innerAw / 2 + railThick / 2, 0, z)
  leftMesh.userData = { type: 'stretcher-rail', side: 'left' }
  leftMesh.renderOrder = renderOrderStretcher
  group.add(leftMesh)

  // Right rail
  const rightGeo = new THREE.BoxGeometry(railThick, sideRailH, depth)
  const rightMesh = new THREE.Mesh(rightGeo, woodMat)
  rightMesh.position.set(innerAw / 2 - railThick / 2, 0, z)
  rightMesh.userData = { type: 'stretcher-rail', side: 'right' }
  rightMesh.renderOrder = renderOrderStretcher
  group.add(rightMesh)

  return group
}

export function updateStretcherBarsGeometry(group, artWidth, artHeight, depthInches, insetInches = 0.1) {
  if (!group) return
  const aw = inches(artWidth)
  const ah = inches(artHeight)
  const railThick = inches(depthInches)
  const inset = inches(insetInches)
  const depth = Math.max(railThick - 2 * inset, 0.01)
  const innerAw = Math.max(aw - 2 * inset, 0.1)
  const innerAh = Math.max(ah - 2 * inset, 0.1)
  const sideRailH = Math.max(innerAh - 2 * railThick, 0.1)
  const z = -(depth / 2 + inset)

  group.children.forEach((mesh) => {
    if (!mesh?.isMesh || mesh.userData?.type !== 'stretcher-rail') return
    const side = mesh.userData.side
    mesh.geometry?.dispose?.()
    if (side === 'top' || side === 'bottom') {
      mesh.geometry = new THREE.BoxGeometry(innerAw, railThick, depth)
      mesh.position.set(0, (side === 'top' ? 1 : -1) * (innerAh / 2 - railThick / 2), z)
    } else {
      mesh.geometry = new THREE.BoxGeometry(railThick, sideRailH, depth)
      mesh.position.set((side === 'right' ? 1 : -1) * (innerAw / 2 - railThick / 2), 0, z)
    }
    mesh.scale.set(1, 1, 1)
  })
}

export function getCanvasBackReturnInches(depthInches) {
  return Math.min(Math.max(Number(depthInches) * 0.5, 0.25), 0.75)
}

function getSubstrateFrontCanvasSize(panelWidth, panelHeight, base = 1536) {
  const aspect = panelWidth / Math.max(panelHeight, 1e-6)
  if (aspect >= 1) {
    return {
      width: base,
      height: Math.max(1, Math.round(base / aspect)),
    }
  }
  return {
    width: Math.max(1, Math.round(base * aspect)),
    height: base,
  }
}

function drawSubstrateArtworkFront({
  canvas,
  color,
  panelWidth,
  panelHeight,
  artworkWidth,
  artworkHeight,
  artworkTexture,
  fullWidth,
  fullHeight,
  cropLeft,
  cropTop,
  scaleX = 1,
  scaleY = 1,
  rotation = 0,
}) {
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  const frontColor = new THREE.Color(Number(color) || 0xffffff)
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = frontColor.getStyle()
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  const image = artworkTexture?.image
  if (!image || artworkWidth <= 0 || artworkHeight <= 0) return

  const destX = ((panelWidth - artworkWidth) / 2 / panelWidth) * canvas.width
  const destY = ((panelHeight - artworkHeight) / 2 / panelHeight) * canvas.height
  const destW = (artworkWidth / panelWidth) * canvas.width
  const destH = (artworkHeight / panelHeight) * canvas.height

  const imgW = image.width || image.naturalWidth || image.videoWidth
  const imgH = image.height || image.naturalHeight || image.videoHeight
  if (!imgW || !imgH) return

  drawCroppedArtworkToRect(ctx, {
    image,
    destX,
    destY,
    destW,
    destH,
    artworkWidth,
    artworkHeight,
    fullWidth,
    fullHeight,
    cropLeft,
    cropTop,
    scaleX,
    scaleY,
    rotation,
  })
}

export function updateSubstrateBackPanelArtwork(group, {
  color,
  panelWidth,
  panelHeight,
  artworkWidth,
  artworkHeight,
  artworkTexture,
  fullWidth,
  fullHeight,
  cropLeft,
  cropTop,
  scaleX = 1,
  scaleY = 1,
  rotation = 0,
} = {}) {
  const frontCanvas = group?.userData?.frontCanvas
  const frontTexture = group?.userData?.frontTexture
  if (!frontCanvas || !frontTexture) return
  const canvasSize = getSubstrateFrontCanvasSize(panelWidth, panelHeight)
  let canvasResized = false
  if (frontCanvas.width !== canvasSize.width) {
    frontCanvas.width = canvasSize.width
    canvasResized = true
  }
  if (frontCanvas.height !== canvasSize.height) {
    frontCanvas.height = canvasSize.height
    canvasResized = true
  }
  if (canvasResized) frontTexture.dispose?.()

  drawSubstrateArtworkFront({
    canvas: frontCanvas,
    color,
    panelWidth,
    panelHeight,
    artworkWidth,
    artworkHeight,
    artworkTexture,
    fullWidth,
    fullHeight,
    cropLeft,
    cropTop,
    scaleX,
    scaleY,
    rotation,
  })
  frontTexture.needsUpdate = true
}

export function updateHangHardwareGeometry(group, {
  innerWidth,
  innerHeight,
  hangType = '',
  substrate = '',
  backDepth = 0.6,
  hardwareWidth = innerWidth,
  hardwareHeight = innerHeight,
  wireAnchorWidth = null,
  securityVerticalInset = null,
} = {}) {
  if (!group) return
  const parent = group.parent
  const replacement = createHangHardware({
    innerWidth,
    innerHeight,
    hangType,
    substrate,
    backDepth,
    hardwareWidth,
    hardwareHeight,
    wireAnchorWidth,
    securityVerticalInset,
  })
  if (!replacement) return

  while (group.children.length) {
    const child = group.children.pop()
    child.traverse?.((obj) => {
      obj.geometry?.dispose?.()
      const mats = obj.material ? (Array.isArray(obj.material) ? obj.material : [obj.material]) : []
      mats.forEach(m => m?.dispose?.())
    })
  }
  replacement.children.slice().forEach(child => group.add(child))
  group.userData = { ...group.userData, ...replacement.userData }
  if (parent && replacement.parent) replacement.parent.remove(replacement)
}

/**
 * Build a thin extruded ring whose only visible surface is the beveled rim.
 * Overlaid on the panel's front face to fake a rounded, highlight-catching
 * edge without touching the artwork-bearing geometry.
 *
 * The flat front/back caps of the extrusion use a fully transparent material
 * (ExtrudeGeometry material index 0); only the curved bevel surface shows
 * (material index 1).
 *
 * The rim material uses additive blending with a black base and full
 * metalness, so only the environment reflection contributes — the rim is
 * invisible where it faces away from the light and glints where it catches it.
 *
 * @param {number} panelWidth  - panel width in inches
 * @param {number} panelHeight - panel height in inches
 * @param {object} [opts]
 * @param {number} [opts.zOffsetInches]
 * @returns {THREE.Mesh|null}
 */
function buildPanelBevelGeometry(panelWidth, panelHeight, { sizeInches } = {}) {
  const sizeIn = sizeInches != null ? sizeInches : panelBevelSettings.sizeInches
  const s = inches(sizeIn)
  if (!(s > 0)) return null

  const w = inches(panelWidth)
  const h = inches(panelHeight)
  const innerW = Math.max(w - 2 * s, 1e-3)
  const innerH = Math.max(h - 2 * s, 1e-3)

  const shape = new THREE.Shape()
  shape.moveTo(-innerW / 2, -innerH / 2)
  shape.lineTo(innerW / 2, -innerH / 2)
  shape.lineTo(innerW / 2, innerH / 2)
  shape.lineTo(-innerW / 2, innerH / 2)
  shape.closePath()

  // depth must be > 0 but tiny — this collapses the straight middle band and
  // lets the two quarter-round bevels meet into a single half-round profile.
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: 1e-5,
    bevelEnabled: true,
    bevelSize: s,
    bevelThickness: s,
    bevelSegments: Math.max(1, Math.round(panelBevelSettings.segments)),
    curveSegments: 1,
    steps: 1,
  })
  geo.computeVertexNormals()
  return geo
}

function buildPanelBevelRimNormalMap(rimWeaveTileInches = panelBevelSettings.canvasNormalTileInches) {
  const base = getCanvasWeaveNormalBaseTexture()
  if (!base) return null
  // ExtrudeGeometry's default UV generator uses world XY positions as UVs, so
  // `repeat = 1 / tileInches` gives 1 weave tile per tileInches in world space
  // — scaled with fabric wrap weave so acoustic rim matches its tighter face weave.
  const tile = Math.max(Number(rimWeaveTileInches) || panelBevelSettings.canvasNormalTileInches, 1e-3)
  const r = 1 / tile
  return createRepeatedCanvasTexture(
    base,
    new THREE.Vector2(r, r),
    new THREE.Vector2(0, 0)
  )
}

function buildPanelBevelMaterials({ useCanvasNormal = false, rimWeaveTileInches, rimCanvasNormalScale } = {}) {
  const capMat = new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: 0,
    depthWrite: false,
  })
  if (panelBevelSettings.debugUnlit) {
    return [capMat, new THREE.MeshBasicMaterial({
      color: panelBevelSettings.debugColor,
      side: THREE.DoubleSide,
    })]
  }

  const rimTile = rimWeaveTileInches ?? panelBevelSettings.canvasNormalTileInches
  const normalMap = useCanvasNormal ? buildPanelBevelRimNormalMap(rimTile) : null
  const normalScale = useCanvasNormal
    ? (Number(rimCanvasNormalScale) || panelBevelSettings.canvasNormalScale)
    : 0

  const rimMat = new THREE.MeshPhysicalMaterial({
    // Black base + metalness → diffuse contributes 0, only the env/clearcoat
    // reflection contributes. Additive blending puts that reflection on top
    // of the underlying artwork without obscuring it.
    color: 0x000000,
    roughness: panelBevelSettings.roughness,
    metalness: panelBevelSettings.metalness,
    clearcoat: panelBevelSettings.clearcoat,
    clearcoatRoughness: panelBevelSettings.clearcoatRoughness,
    envMapIntensity: panelBevelSettings.intensity,
    normalMap,
    normalScale: new THREE.Vector2(normalScale, normalScale),
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.FrontSide,
  })

  // Scale the final output so `intensity` affects every lobe (including
  // clearcoat, which is not covered by envMapIntensity). The uniform is kept
  // on the material so the debug panel can update it without recompiling.
  rimMat.onBeforeCompile = (shader) => {
    shader.uniforms.uBevelIntensity = { value: panelBevelSettings.intensity }
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <output_fragment>',
      [
        '#include <output_fragment>',
        'gl_FragColor.rgb *= uBevelIntensity;',
      ].join('\n')
    )
    rimMat.userData.shader = shader
  }

  return [capMat, rimMat]
}

function createPanelBevelRim(panelWidth, panelHeight, opts = {}) {
  const sizeOverride = opts.sizeInches
  const geo = buildPanelBevelGeometry(panelWidth, panelHeight, { sizeInches: sizeOverride })
  if (!geo) return null

  const useCanvasNormal = !!opts.useCanvasNormal
  const fabricWrapWeaveTileInches = opts.fabricWrapWeaveTileInches ?? GALLERY_WRAP_CANVAS_FABRIC_WEAVE_TILE_INCHES
  const fabricWeaveNormalScale = opts.fabricWeaveNormalScale ?? GALLERY_WRAP_CANVAS_FABRIC_WEAVE_NORMAL_SCALE
  const rimWeaveTileInches = useCanvasNormal
    ? panelBevelSettings.canvasNormalTileInches
      * (fabricWrapWeaveTileInches / GALLERY_WRAP_CANVAS_FABRIC_WEAVE_TILE_INCHES)
    : undefined
  const rimCanvasNormalScale = useCanvasNormal
    ? panelBevelSettings.canvasNormalScale
      * (fabricWeaveNormalScale / GALLERY_WRAP_CANVAS_FABRIC_WEAVE_NORMAL_SCALE)
    : undefined
  const materials = buildPanelBevelMaterials({ useCanvasNormal, rimWeaveTileInches, rimCanvasNormalScale })
  const mesh = new THREE.Mesh(geo, materials)
  mesh.position.set(0, 0, inches(panelBevelSettings.zOffsetInches))
  mesh.userData = {
    type: 'panel-bevel-rim',
    panelWidth,
    panelHeight,
    useCanvasNormal,
    fabricWrapWeaveTileInches: useCanvasNormal ? fabricWrapWeaveTileInches : undefined,
    fabricWeaveNormalScale: useCanvasNormal ? fabricWeaveNormalScale : undefined,
    sizeInchesOverride: sizeOverride != null ? sizeOverride : null,
  }
  mesh.renderOrder = 2
  return mesh
}

/**
 * Live-update every panel bevel rim in the given object tree after mutating
 * `panelBevelSettings`. Rebuilds geometry (for size/segments changes) and
 * replaces materials (for everything else) in place.
 *
 * @param {THREE.Object3D} root
 */
export function refreshPanelBevelRims(root) {
  if (!root) return
  root.traverse((obj) => {
    if (obj.userData?.type !== 'panel-bevel-rim') return
    const { panelWidth, panelHeight } = obj.userData
    if (panelWidth == null || panelHeight == null) return

    const sizeOverride = obj.userData.sizeInchesOverride
    const newGeo = buildPanelBevelGeometry(panelWidth, panelHeight, {
      sizeInches: sizeOverride != null ? sizeOverride : undefined,
    })
    if (obj.geometry) obj.geometry.dispose()
    if (newGeo) obj.geometry = newGeo

    const oldMats = Array.isArray(obj.material) ? obj.material : [obj.material]
    oldMats.forEach((m) => {
      if (m?.normalMap) m.normalMap.dispose?.()
      m?.dispose?.()
    })
    const wrapTile = obj.userData.fabricWrapWeaveTileInches ?? GALLERY_WRAP_CANVAS_FABRIC_WEAVE_TILE_INCHES
    const faceNormalScale = obj.userData.fabricWeaveNormalScale ?? GALLERY_WRAP_CANVAS_FABRIC_WEAVE_NORMAL_SCALE
    const useCanvasNormal = !!obj.userData.useCanvasNormal
    const rimWeaveTileInches = useCanvasNormal
      ? panelBevelSettings.canvasNormalTileInches * (wrapTile / GALLERY_WRAP_CANVAS_FABRIC_WEAVE_TILE_INCHES)
      : undefined
    const rimCanvasNormalScale = useCanvasNormal
      ? panelBevelSettings.canvasNormalScale * (faceNormalScale / GALLERY_WRAP_CANVAS_FABRIC_WEAVE_NORMAL_SCALE)
      : undefined
    obj.material = buildPanelBevelMaterials({
      useCanvasNormal,
      rimWeaveTileInches,
      rimCanvasNormalScale,
    })
    obj.position.set(0, 0, inches(panelBevelSettings.zOffsetInches))
  })
}

/**
 * Apply the current `panelBevelSettings.intensity` to every compiled rim
 * shader without rebuilding materials. Cheaper than refreshPanelBevelRims for
 * scrubbing the intensity slider.
 *
 * @param {THREE.Object3D} root
 */
export function syncPanelBevelIntensity(root) {
  if (!root) return
  root.traverse((obj) => {
    if (obj.userData?.type !== 'panel-bevel-rim') return
    const mats = Array.isArray(obj.material) ? obj.material : [obj.material]
    mats.forEach((m) => {
      const uniforms = m?.userData?.shader?.uniforms
      if (uniforms?.uBevelIntensity) {
        uniforms.uBevelIntensity.value = panelBevelSettings.intensity
      }
      if (m && 'envMapIntensity' in m) {
        m.envMapIntensity = panelBevelSettings.intensity
      }
    })
  })
}

/**
 * Create a substrate back panel (for Aluminum, Acrylic, Acoustic Panel, Gator Board).
 * Use a simple solid box so front, back, and edges all render normally with no
 * special depth-write handling.
 *
 * @param {number} artWidth  - artwork width in inches
 * @param {number} artHeight - artwork height in inches
 * @param {object} opts
 * @param {number} opts.depth     - substrate depth in inches
 * @param {number} opts.color     - hex color (e.g. 0xc0c0c0)
 * @param {number} opts.roughness - 0-1 (default 0.5)
 * @param {number} opts.metalness - 0-1 (default 0)
 * @param {boolean} opts.hasSides - retained for API compatibility; solid geometry always has edges
 * @param {THREE.Texture|null} opts.artworkTexture - optional artwork rendered directly onto the front face
 * @param {number} opts.artworkWidth - visible artwork width in inches
 * @param {number} opts.artworkHeight - visible artwork height in inches
 * @param {number} opts.fullWidth - cropped source width in artwork-space inches
 * @param {number} opts.fullHeight - cropped source height in artwork-space inches
 * @param {number} opts.cropLeft - crop origin from left in artwork-space inches
 * @param {number} opts.cropTop - crop origin from top in artwork-space inches
 * @returns {THREE.Group}
 */
export function createSubstrateBackPanel(artWidth, artHeight, {
  depth,
  color,
  roughness = 0.5,
  metalness = 0,
  hasSides = false,
  bevelRim = true,
  artworkTexture = null,
  artworkWidth = 0,
  artworkHeight = 0,
  fullWidth = null,
  fullHeight = null,
  cropLeft = null,
  cropTop = null,
  scaleX = 1,
  scaleY = 1,
  rotation = 0,
} = {}) {
  const group = new THREE.Group()
  group.userData = { type: 'substrate-back' }

  const w = inches(artWidth)
  const h = inches(artHeight)
  const d = Math.max(inches(depth), inches(0.01))

  const sideMat = new THREE.MeshStandardMaterial({
    color: Number(color),
    roughness: Number(roughness),
    metalness: Number(metalness),
    side: THREE.FrontSide,
  })
  const backMat = sideMat.clone()

  let frontMat = sideMat.clone()
  let frontCanvas = null
  let frontTexture = null
  if (artworkTexture && artworkWidth > 0 && artworkHeight > 0) {
    const canvasSize = getSubstrateFrontCanvasSize(artWidth, artHeight)
    frontCanvas = document.createElement('canvas')
    frontCanvas.width = canvasSize.width
    frontCanvas.height = canvasSize.height

    drawSubstrateArtworkFront({
      canvas: frontCanvas,
      color,
      panelWidth: artWidth,
      panelHeight: artHeight,
      artworkWidth,
      artworkHeight,
      artworkTexture,
      fullWidth,
      fullHeight,
      cropLeft,
      cropTop,
      scaleX,
      scaleY,
      rotation,
    })

    frontTexture = new THREE.CanvasTexture(frontCanvas)
    frontTexture.colorSpace = THREE.SRGBColorSpace
    frontTexture.wrapS = THREE.ClampToEdgeWrapping
    frontTexture.wrapT = THREE.ClampToEdgeWrapping
    frontTexture.needsUpdate = true

    frontMat = new THREE.MeshStandardMaterial({
      map: frontTexture,
      color: 0xffffff,
      roughness: Number(roughness),
      metalness: Number(metalness),
      side: THREE.FrontSide,
    })
  }

  const substrateMesh = new THREE.Mesh(
    new THREE.BoxGeometry(w, h, d),
    [sideMat, sideMat, sideMat, sideMat, frontMat, backMat]
  )
  substrateMesh.position.set(0, 0, -d / 2)
  substrateMesh.userData = {
    type: frontTexture ? 'artwork' : 'substrate-back-solid',
    hasSides: Boolean(hasSides),
    integratedArtwork: Boolean(frontTexture),
    widthInches: artworkWidth || artWidth,
    heightInches: artworkHeight || artHeight,
    label: frontTexture ? 'Artwork' : 'Substrate',
  }
  group.add(substrateMesh)

  if (bevelRim) {
    const rim = createPanelBevelRim(artWidth, artHeight, {
      sizeInches: RIGID_PANEL_BEVEL_SIZE_INCHES,
    })
    if (rim) group.add(rim)
  }

  group.userData.frontCanvas = frontCanvas
  group.userData.frontTexture = frontTexture
  group.userData.artworkTexture = artworkTexture
  group.userData.integratedArtwork = Boolean(frontTexture)

  return group
}

/**
 * Create a mat border with per-side widths.
 *
 * The mat sits between the artwork and the frame as a colored border.
 * We build it from 4 thin box pieces (like frame rails but flat).
 *
 * @param {object} opts
 * @param {number} opts.artWidth    - artwork width in inches
 * @param {number} opts.artHeight   - artwork height in inches
 * @param {number} opts.matTop      - mat width for top in inches
 * @param {number} opts.matBottom   - mat width for bottom in inches
 * @param {number} opts.matLeft     - mat width for left in inches
 * @param {number} opts.matRight    - mat width for right in inches
 * @param {string} opts.color       - CSS hex color e.g. '#FFFFFF'
 * @param {number} opts.depth       - mat thickness in inches (default 0.0625 = 1/16")
 * @returns {THREE.Group}
 */
export function createMatBorder({
  artWidth,
  artHeight,
  matTop = 0,
  matBottom = 0,
  matLeft = 0,
  matRight = 0,
  color = '#FFFFFF',
  depth = DEFAULT_MAT_DEPTH,
  coreColor = null,
}) {
  const group = new THREE.Group()
  group.userData = { type: 'mat' }

  const innerBounds = {
    left: -inches(artWidth) / 2,
    right: inches(artWidth) / 2,
    top: inches(artHeight) / 2,
    bottom: -inches(artHeight) / 2,
  }
  const { mesh, outerBounds } = createMatMesh({
    innerBounds,
    widths: { top: matTop, bottom: matBottom, left: matLeft, right: matRight },
    color,
    depth,
    coreColor,
    matNum: 1,
  })

  group.add(mesh)
  group.userData.fullWidth = outerBounds.right - outerBounds.left
  group.userData.fullHeight = outerBounds.top - outerBounds.bottom

  return group
}

/**
 * Create stacked mat borders. Order back-to-front: image, Mat 3, Mat 2, Mat 1.
 * Each underlying mat width is the visible stickout beyond the mat on top of it.
 *
 * @param {object} opts
 * @param {number} opts.artWidth   - artwork width in inches
 * @param {number} opts.artHeight  - artwork height in inches
 * @param {Array<{color: string, depth?: number, coreColor?: string|null}>} opts.mats - [{ color }, ...] Mat 1, Mat 2, Mat 3 (outer to inner)
 * @param {Array<{top, bottom, left, right}>} opts.widths - per-mat widths in inches
 * @param {Array<number>} opts.depths - per-mat thicknesses in inches (default 0.0625)
 * @returns {THREE.Group}
 */
export function createMultiMatBorder({ artWidth, artHeight, mats = [], widths = [], depths = [] }) {
  const group = new THREE.Group()
  group.userData = { type: 'mat', multi: true }

  if (!mats.length || !widths.length) return group

  // Build from back to front: Mat 3 (innermost), then Mat 2, then Mat 1 (outermost)
  // mats[0]=Mat1, mats[1]=Mat2, mats[2]=Mat3
  let innerBounds = {
    left: -inches(artWidth) / 2,
    right: inches(artWidth) / 2,
    top: inches(artHeight) / 2,
    bottom: -inches(artHeight) / 2,
  }
  let outerBounds = { ...innerBounds }
  let zOffset = 0

  for (let idx = mats.length - 1; idx >= 0; idx--) {
    const w = widths[idx] || { top: 2, bottom: 2, left: 2, right: 2 }
    const d = depths[idx] != null ? depths[idx] : DEFAULT_MAT_DEPTH
    const { mesh, outerBounds: nextOuterBounds } = createMatMesh({
      innerBounds,
      widths: w,
      color: mats[idx]?.color || '#FFFFFF',
      depth: Number(mats[idx]?.depth) > 0 ? Number(mats[idx].depth) : d,
      coreColor: mats[idx]?.coreColor || null,
      matNum: idx + 1,
    })
    mesh.position.z = zOffset
    group.add(mesh)
    outerBounds = nextOuterBounds
    innerBounds = nextOuterBounds
    zOffset += inches(d)
  }

  group.userData.fullWidth = outerBounds.right - outerBounds.left
  group.userData.fullHeight = outerBounds.top - outerBounds.bottom
  return group
}

export function updateMatBorderGeometry(group, artWidth, artHeight) {
  if (!group) return
  let innerBounds = {
    left: -inches(artWidth) / 2,
    right: inches(artWidth) / 2,
    top: inches(artHeight) / 2,
    bottom: -inches(artHeight) / 2,
  }
  let outerBounds = { ...innerBounds }

  group.children.forEach((mesh) => {
    if (!mesh?.isMesh || mesh.userData?.type !== 'mat-piece') return
    const { geometry, outerBounds: nextOuterBounds, safeWidths } = createMatGeometry({
      innerBounds,
      widths: mesh.userData?.widthsInches || {},
      depth: mesh.userData?.depthInches || DEFAULT_MAT_DEPTH,
    })
    mesh.geometry?.dispose?.()
    mesh.geometry = geometry
    mesh.scale.set(1, 1, 1)
    mesh.userData = {
      ...mesh.userData,
      widthInches: safeWidths.top,
      widthsInches: { ...safeWidths },
      innerBounds: { ...innerBounds },
      outerBounds: { ...nextOuterBounds },
    }
    outerBounds = nextOuterBounds
    innerBounds = nextOuterBounds
  })

  group.scale.set(1, 1, 1)
  group.userData.fullWidth = outerBounds.right - outerBounds.left
  group.userData.fullHeight = outerBounds.top - outerBounds.bottom
}

// ─── LINEN LINER ─────────────────────────────────────────────────────────────
// A fabric-covered liner that sits between the artwork and the frame on framed
// stretched canvas. In cross-section it is the middle layer: the canvas panel
// sits lowest, the liner steps up from it with a bevelled inner edge, and the
// wood moulding steps up again around that.
//
// Built exactly the way mats are — a rect-with-hole extrusion whose inner edge
// is chamfered at 45° (`bevelSize === bevelThickness`) — just several times
// thicker, so it reads as a liner stepping up to the frame rather than as a
// sheet of mat board. The linen itself is the shared fabric weave normal map at
// a fine tile, over a warm off-white.

/** Liner thickness front-to-back, in inches. A mat is 1/16"; this is 1/4". */
export const LINEN_LINER_DEPTH = 0.25
/**
 * How far a frame's face stands proud of a liner seated in its rabbet, in
 * inches. The catalogue gives a frame's rabbet depth but not its overall depth,
 * so this is the step we choose to render rather than one we can read.
 */
export const FRAME_LIP_OVER_LINER = 0.25
/** Keeps the liner's back face off the artwork plane so the two cannot z-fight. */
export const LINEN_LINER_ARTWORK_CLEARANCE = 0.01
/** Linen face colour — warm off-white. */
const LINEN_LINER_COLOR = '#E3DCD0'
/** Inches per weave tile. Linen is a finer, tighter weave than canvas. */
const LINEN_LINER_WEAVE_TILE_INCHES = 1.1
/** `normalScale` for the weave: enough to read as cloth, not as relief. */
const LINEN_LINER_WEAVE_NORMAL_SCALE = 0.3

function createLinenLinerMaterials(widthInches, heightInches) {
  const weaveNormalBase = getCanvasWeaveNormalBaseTexture()
  const { repeat, offset } = weaveNormalUVs(widthInches, heightInches, LINEN_LINER_WEAVE_TILE_INCHES)
  const normalMap = weaveNormalBase ? createRepeatedCanvasTexture(weaveNormalBase, repeat, offset) : null

  const face = new THREE.MeshStandardMaterial({
    color: new THREE.Color(LINEN_LINER_COLOR),
    roughness: 0.95,
    metalness: 0,
  })
  if (normalMap) {
    face.normalMap = normalMap
    face.normalScale = new THREE.Vector2(LINEN_LINER_WEAVE_NORMAL_SCALE, LINEN_LINER_WEAVE_NORMAL_SCALE)
  }
  // The chamfer turns away from the light, so it reads darker — the same trick
  // the mat core colour plays. Also used for the hidden outer wall and back.
  const bevel = new THREE.MeshStandardMaterial({
    color: new THREE.Color(LINEN_LINER_COLOR).multiplyScalar(0.88),
    roughness: 0.95,
    metalness: 0,
  })
  return [face, bevel]
}

function linenLinerInnerBounds(artWidth, artHeight) {
  return {
    left: -inches(artWidth) / 2,
    right: inches(artWidth) / 2,
    top: inches(artHeight) / 2,
    bottom: -inches(artHeight) / 2,
  }
}

/** A rectangle's corners, counter-clockwise seen from +z, at a given depth. */
function linenLinerRingCorners({ left, right, top, bottom }, z) {
  return [
    [left, bottom, z],
    [right, bottom, z],
    [right, top, z],
    [left, top, z],
  ]
}

/**
 * Liner band geometry: a rectangular ring chamfered at 45° on its inner edge
 * only. `ExtrudeGeometry` (what mats use) bevels every contour including the
 * outer one, which leaves a slope where the liner meets the frame; the liner's
 * outer edge butts into the rabbet and has to stay square, so the ring is built
 * directly here from four loops:
 *
 *     outer@back ── outer@front ─────────────┐  front face (flat, linen)
 *          │                                 └─ inner@front ┐
 *          │  square outer wall                             │ 45° chamfer
 *     outer@back ─── back face ─── inner@back ───────────────┘
 */
function createLinenLinerGeometry({ innerBounds, width, depth }) {
  const w = Math.max(1e-4, Number(width) || 0)
  const d = Math.max(1e-4, Number(depth) || 0)
  // 45°: the chamfer travels the same distance across as it does back.
  const chamfer = Math.min(d, w)

  const outer = {
    left: innerBounds.left - w,
    right: innerBounds.right + w,
    top: innerBounds.top + w,
    bottom: innerBounds.bottom - w,
  }
  // The opening flares toward the viewer, so the front opening is the wider one.
  const innerFront = {
    left: innerBounds.left - chamfer,
    right: innerBounds.right + chamfer,
    top: innerBounds.top + chamfer,
    bottom: innerBounds.bottom - chamfer,
  }

  const outerBack = linenLinerRingCorners(outer, 0)
  const outerFront = linenLinerRingCorners(outer, d)
  const openingFront = linenLinerRingCorners(innerFront, d)
  const openingBack = linenLinerRingCorners(innerBounds, 0)

  const positions = []
  const uvs = []
  const spanX = outer.right - outer.left
  const spanY = outer.top - outer.bottom

  const pushVertex = ([x, y, z]) => {
    positions.push(x, y, z)
    uvs.push((x - outer.left) / spanX, (y - outer.bottom) / spanY)
  }
  // Quads run A[i] → A[i+1] → B[i+1] → B[i]; this winding faces outward for
  // every band below, so vertex normals come out right without any flipping.
  const addBand = (a, b) => {
    for (let i = 0; i < 4; i++) {
      const j = (i + 1) % 4
      pushVertex(a[i]); pushVertex(a[j]); pushVertex(b[j])
      pushVertex(a[i]); pushVertex(b[j]); pushVertex(b[i])
    }
  }

  const bandStarts = []
  const mark = () => bandStarts.push(positions.length / 3)

  mark(); addBand(outerFront, openingFront) // front face — the linen
  mark(); addBand(openingFront, openingBack) // inner chamfer
  mark(); addBand(outerBack, outerFront) // outer wall, square
  mark(); addBand(openingBack, outerBack) // back
  const total = positions.length / 3

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
  geometry.addGroup(bandStarts[0], bandStarts[1] - bandStarts[0], 0)
  geometry.addGroup(bandStarts[1], total - bandStarts[1], 1)
  geometry.computeVertexNormals()

  return { geometry, outerBounds: outer, safeWidths: { top: w, bottom: w, left: w, right: w } }
}

/**
 * Create the linen liner band that wraps the artwork.
 *
 * @param {object} opts
 * @param {number} opts.artWidth   - width of what the liner wraps, in inches
 * @param {number} opts.artHeight  - height of what the liner wraps, in inches
 * @param {number} opts.linerWidth - liner width per side, in inches (1 or 2)
 * @param {number} [opts.depth]    - liner thickness, in inches
 * @returns {THREE.Group|null}
 */
export function createLinenLiner({ artWidth, artHeight, linerWidth, depth = LINEN_LINER_DEPTH }) {
  const w = Math.max(0, Number(linerWidth) || 0)
  if (!(w > 0)) return null

  const group = new THREE.Group()
  group.userData = { type: 'linen-liner' }

  const innerBounds = linenLinerInnerBounds(artWidth, artHeight)
  const { geometry, outerBounds, safeWidths } = createLinenLinerGeometry({ innerBounds, width: w, depth })

  const mesh = new THREE.Mesh(
    geometry,
    createLinenLinerMaterials(Number(artWidth) + 2 * w, Number(artHeight) + 2 * w)
  )
  mesh.userData = {
    type: 'linen-liner-band',
    label: 'Linen Liner',
    widthInches: safeWidths.top,
    widthsInches: { ...safeWidths },
    innerBounds: { ...innerBounds },
    outerBounds: { ...outerBounds },
    depthInches: depth,
  }
  group.add(mesh)

  group.userData.fullWidth = outerBounds.right - outerBounds.left
  group.userData.fullHeight = outerBounds.top - outerBounds.bottom
  return group
}

/** Rebuild the liner band for a new interior size, keeping its materials. */
export function updateLinenLinerGeometry(group, artWidth, artHeight) {
  if (!group) return
  const innerBounds = linenLinerInnerBounds(artWidth, artHeight)

  group.children.forEach((mesh) => {
    if (!mesh?.isMesh || mesh.userData?.type !== 'linen-liner-band') return
    const { geometry, outerBounds, safeWidths } = createLinenLinerGeometry({
      innerBounds,
      width: mesh.userData?.widthsInches?.top ?? mesh.userData?.widthInches,
      depth: mesh.userData?.depthInches || LINEN_LINER_DEPTH,
    })
    mesh.geometry?.dispose?.()
    mesh.geometry = geometry
    mesh.scale.set(1, 1, 1)
    mesh.userData = {
      ...mesh.userData,
      widthInches: safeWidths.top,
      widthsInches: { ...safeWidths },
      innerBounds: { ...innerBounds },
      outerBounds: { ...outerBounds },
    }
    group.userData.fullWidth = outerBounds.right - outerBounds.left
    group.userData.fullHeight = outerBounds.top - outerBounds.bottom
  })

  group.scale.set(1, 1, 1)
}

/**
 * Create corner resize handles (small spheres at the 4 corners of the artwork).
 *
 * @param {number} artWidth  - artwork width in inches
 * @param {number} artHeight - artwork height in inches
 * @returns {THREE.Group}
 */
/**
 * The interaction overlay's materials are configuration-constant — the same
 * colours and flags for every item — but the overlay itself is rebuilt whenever
 * the piece's dimensions change. Creating them per rebuild meant the old ones
 * were disposed, which dropped the last reference to their compiled shader
 * program; three then deleted it and the next draw recompiled the identical
 * shader, blocking the main thread inside getUniforms() waiting on the link.
 *
 * Callers keep one of each alive for their lifetime and pass it in, so the
 * programs survive rebuilds. Materials made this way are flagged so the dispose
 * helpers leave them alone.
 */
// Membership rather than a userData flag on purpose: Material.clone() copies
// userData, so a flag would make every clone of a shared material look shared
// too, and the dispose helpers would skip them for the life of the page.
const sharedOverlayMaterials = new WeakSet()

function sharedOverlayMaterial(material) {
  sharedOverlayMaterials.add(material)
  return material
}

export function createResizeHandleMaterial() {
  return sharedOverlayMaterial(new THREE.MeshStandardMaterial({
    color: 0x2196F3,
    emissive: 0x1565C0,
    emissiveIntensity: 0.3,
    transparent: true,
    opacity: 0, // shown only when hovering framed image
    depthTest: false,
  }))
}

export function createDimensionLineMaterial() {
  return sharedOverlayMaterial(new THREE.LineBasicMaterial({ color: 0x2196F3, linewidth: 1 }))
}

export function createSourceHandleMaterials() {
  return {
    center: sharedOverlayMaterial(new THREE.MeshStandardMaterial({
      color: 0x00bcd4,
      emissive: 0x00838f,
      emissiveIntensity: 0.4,
      transparent: true,
      opacity: 0,
      depthTest: false,
    })),
    scale: sharedOverlayMaterial(new THREE.MeshStandardMaterial({
      color: 0x8bc34a,
      emissive: 0x558b2f,
      emissiveIntensity: 0.4,
      transparent: true,
      opacity: 0,
      depthTest: false,
    })),
    link: sharedOverlayMaterial(new THREE.MeshStandardMaterial({
      color: 0xcccccc,
      transparent: true,
      opacity: 0,
      depthTest: false,
    })),
    cross: sharedOverlayMaterial(new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthTest: false,
    })),
  }
}

/** True for materials that outlive the object graph they are attached to. */
export function isSharedOverlayMaterial(material) {
  return Boolean(material) && sharedOverlayMaterials.has(material)
}

export function createResizeHandles(artWidth, artHeight, cornerMaterials = null) {
  const group = new THREE.Group()
  group.userData = { type: 'handles' }

  const aw = inches(artWidth)
  const ah = inches(artHeight)
  const handleSize = inches(1) // radius (2" diameter)
  const handleGeo = new THREE.SphereGeometry(handleSize, 16, 16)

  const corners = [
    { x: -aw / 2, y: ah / 2, name: 'top-left' },
    { x: aw / 2, y: ah / 2, name: 'top-right' },
    { x: -aw / 2, y: -ah / 2, name: 'bottom-left' },
    { x: aw / 2, y: -ah / 2, name: 'bottom-right' },
  ]

  corners.forEach((c, index) => {
    // One material per corner: hover brightens the corner under the pointer, not
    // all four. Callers pass a set they keep across rebuilds so the shader
    // program survives; without one we own them and they are disposed normally.
    const handle = new THREE.Mesh(handleGeo, cornerMaterials?.[index] || createResizeHandleMaterial())
    handle.position.set(c.x, c.y, 0.5)
    handle.castShadow = false
    handle.userData = {
      type: 'resize-handle',
      corner: c.name,
      label: `Resize (${c.name})`,
    }
    group.add(handle)
  })

  return group
}

/**
 * Create dimension annotation lines (edges of the framed piece with length labels).
 * Returns a Group containing Line objects.
 *
 * These are toggled visible on hover via the interaction layer.
 * When outerWidth/outerHeight are provided, lines span the finished size.
 *
 * @param {number} artWidth
 * @param {number} artHeight
 * @param {number} frameOffset - offset from art edge (frame + mat width) to place lines outside
 * @param {number} [outerWidth] - finished width in inches (uses artWidth if omitted)
 * @param {number} [outerHeight] - finished height in inches (uses artHeight if omitted)
 * @returns {THREE.Group}
 */
export function createDimensionLines(artWidth, artHeight, frameOffset = 0, outerWidth, outerHeight, sharedLineMaterial = null) {
  const group = new THREE.Group()
  group.userData = { type: 'dimensions' }
  group.visible = false // hidden by default

  const ow = inches(outerWidth ?? artWidth)
  const oh = inches(outerHeight ?? artHeight)
  const offset = inches(frameOffset) + 0.5

  const lineMat = sharedLineMaterial || createDimensionLineMaterial()

  // Width line (bottom) - span outer width, positioned below outer edge
  const wGeo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(-ow / 2, -oh / 2 - offset, 0.1),
    new THREE.Vector3(ow / 2, -oh / 2 - offset, 0.1),
  ])
  const wLine = new THREE.Line(wGeo, lineMat)
  wLine.userData = { type: 'dim-line', axis: 'width', valueInches: outerWidth ?? artWidth }
  group.add(wLine)

  // Height line (right) - span outer height
  const hGeo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(ow / 2 + offset, -oh / 2, 0.1),
    new THREE.Vector3(ow / 2 + offset, oh / 2, 0.1),
  ])
  const hLine = new THREE.Line(hGeo, lineMat)
  hLine.userData = { type: 'dim-line', axis: 'height', valueInches: outerHeight ?? artHeight }
  group.add(hLine)

  return group
}

/**
 * Create outline rectangles for standard snap sizes and resize constraints,
 * centered on the artwork origin.
 *
 * @param {Array<{label?: string, value?: {w?: number, h?: number}}>} snapCandidates
 * @param {Array<{label?: string, width?: number, height?: number, maxWidth?: number, maxHeight?: number, type?: string}>} constraints
 * @returns {THREE.Group}
 */
export function createSizeSnapOverlay(snapCandidates = [], constraints = []) {
  const group = new THREE.Group()
  group.userData = { type: 'size-snap-overlay' }
  group.visible = false

  const makeRectPoints = (width, height, inset = 0) => {
    const halfW = Math.max(0.01, inches(width) / 2 - inset)
    const halfH = Math.max(0.01, inches(height) / 2 - inset)
    return [
      new THREE.Vector3(-halfW, -halfH, 0.22),
      new THREE.Vector3(halfW, -halfH, 0.22),
      new THREE.Vector3(halfW, halfH, 0.22),
      new THREE.Vector3(-halfW, halfH, 0.22),
    ]
  }

  const addOutline = ({
    width,
    height,
    label,
    color,
    type,
    severity = null,
    constraint = null,
    orientation = null,
  }) => {
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return
    }

    const geometry = new THREE.BufferGeometry().setFromPoints(makeRectPoints(width, height))
    const material = new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity: 0,
      depthTest: false,
    })
    const line = new THREE.LineLoop(geometry, material)
    line.userData = {
      type,
      width,
      height,
      label: label || `${width} x ${height}`,
      severity,
      constraint,
      orientation,
      baseOverlayOpacity: 1,
      overlayOpacity: 0,
    }
    group.add(line)
  }

  snapCandidates.forEach((candidate) => {
    addOutline({
      width: Number(candidate?.value?.w),
      height: Number(candidate?.value?.h),
      label: candidate?.label,
      color: 0x00e5ff,
      type: 'size-snap-outline',
    })
  })

  const constraintOutlineKeys = new Set()
  constraints.forEach((constraint) => {
    const orientations = Array.isArray(constraint?.maxOrientations) && constraint.maxOrientations.length
      ? constraint.maxOrientations
      : [{ width: constraint?.maxWidth ?? constraint?.width, height: constraint?.maxHeight ?? constraint?.height }]
    orientations.forEach((orientation) => {
      const width = Number(orientation?.width ?? orientation?.maxWidth)
      const height = Number(orientation?.height ?? orientation?.maxHeight)
      const severity = constraint?.severity || 'error'
      const key = `${severity}:${Math.round(width * 1000)}x${Math.round(height * 1000)}`
      if (constraintOutlineKeys.has(key)) return
      constraintOutlineKeys.add(key)
      addOutline({
        width,
        height,
        label: constraint?.label,
        color: severity === 'warning' ? 0xffcc00 : 0xff3b30,
        type: 'size-constraint-outline',
        severity,
        constraint,
        orientation: { width, height },
      })
    })
  })

  return group
}

/**
 * Compute the overall outer dimensions of a framed piece.
 * Supports per-side mat widths (matTop/Bottom/Left/Right).
 * When substrateBorder is set (e.g. acrylic border inset), adds 2*border to each dimension.
 */
export function computeOuterDimensions({
  artWidth, artHeight,
  matTop = 0, matBottom = 0, matLeft = 0, matRight = 0,
  frameWidth = 0,
  substrateBorder = 0,
  linerWidth = 0,
}) {
  const frameEdge = frameWidth > 0 ? frameWidth * 2 : 0
  const borderEdge = (substrateBorder || 0) * 2
  // The linen liner is a physical layer around the art, so it counts on all
  // four sides — legacy adds it twice per axis in `pricing_dimensions`.
  const linerEdge = (linerWidth > 0 ? linerWidth : 0) * 2
  return {
    outerWidth: Number(artWidth) + matLeft + matRight + frameEdge + borderEdge + linerEdge,
    outerHeight: Number(artHeight) + matTop + matBottom + frameEdge + borderEdge + linerEdge,
  }
}
